// TransportSessionMapModal.js
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import maplibregl from "maplibre-gl";
import Sortable from "sortablejs";
import { FaGripLines } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import axios from "../axiosConfig";
import { canManageRouteTemplates } from "../lib/routePermissions";
import "maplibre-gl/dist/maplibre-gl.css";
import "../styles/TransportSessionMapModal.css";

const MAP_STYLE = "https://tiles.openfreemap.org/styles/bright";

const PREVIEW_ROUTE_SOURCE_ID = "transport-session-route-preview";
const PREVIEW_ROUTE_CASING_LAYER_ID = "transport-session-route-preview-casing";
const PREVIEW_ROUTE_LINE_LAYER_ID = "transport-session-route-preview-line";

function toNumber(value) {
  if (value === undefined || value === null || value === "") return null;

  const n = Number(String(value).replace(",", "."));
  return Number.isFinite(n) ? n : null;
}

function escapeHtml(value) {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function normalizeLngLatFromPair(a, b) {
  const n1 = toNumber(a);
  const n2 = toNumber(b);

  if (n1 === null || n2 === null) return null;

  // App/common format: lat,lng
  if (Math.abs(n1) <= 90 && Math.abs(n2) <= 180) {
    return {
      lat: n1,
      lng: n2,
    };
  }

  // GeoJSON/MapLibre format: lng,lat
  if (Math.abs(n1) <= 180 && Math.abs(n2) <= 90) {
    return {
      lat: n2,
      lng: n1,
    };
  }

  return null;
}

function parseCoordinates(point) {
  if (!point) return null;

  const directLat =
    point.lat ??
    point.latitude ??
    point.placeLatitude ??
    point.latestStatusEvent?.placeLatitude ??
    null;

  const directLng =
    point.lng ??
    point.lon ??
    point.longitude ??
    point.placeLongitude ??
    point.latestStatusEvent?.placeLongitude ??
    null;

  const direct = normalizeLngLatFromPair(directLat, directLng);
  if (direct) return direct;

  const value = point.coordinates;

  if (!value) return null;

  if (Array.isArray(value) && value.length >= 2) {
    return normalizeLngLatFromPair(value[0], value[1]);
  }

  if (typeof value === "object") {
    return normalizeLngLatFromPair(
      value.lat ?? value.latitude ?? value[1],
      value.lng ?? value.lon ?? value.longitude ?? value[0]
    );
  }

  const raw = String(value).trim();
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw);
    return parseCoordinates({ ...point, coordinates: parsed });
  } catch {}

  const matches = raw.match(/-?\d+(\.\d+)?/g);
  if (!matches || matches.length < 2) return null;

  return normalizeLngLatFromPair(matches[0], matches[1]);
}

function getPointTypeLabel(type, t) {
  const clean = String(type || "").toUpperCase();

  if (clean === "PICKUP") return t("points.types.pickup");
  if (clean === "DELIVERY") return t("points.types.delivery");
  if (clean === "STRATEGICPOINT") return t("points.types.strategicPoint");

  return clean || t("points.types.point");
}

function getPointStatusLabel(status, t) {
  const clean = String(status || "").toUpperCase();

  if (!clean) return "";

  return t(`points.statuses.${clean}`, {
    defaultValue: String(status || ""),
  });
}

function getEffectivePointStatus(point) {
  return String(
    point?.latestStatusEvent?.newStatus ??
      point?.latestStatusEvent?.status ??
      point?.substatus ??
      point?.status ??
      "PENDING"
  ).toUpperCase();
}

function getPointStatusClass(point) {
  const status = getEffectivePointStatus(point);

  if (status === "COMPLETED") return "completed";
  if (status === "CANCELLED") return "cancelled";
  if (status === "DELAYED") return "delayed";

  if (
    [
      "IN_TRANSIT_MOVING_TO_PICKUP_LOCATION",
      "IN_TRANSIT_MOVING_TO_DELIVERY_LOCATION",
      "AT_PICKUP_LOCATION",
      "AT_DELIVERY_LOCATION",
      "WAITING_TO_LOAD",
      "LOADING_IN_PROGRESS",
      "WAITING_TO_UNLOAD",
      "UNLOADING_MERCHANDISE",
    ].includes(status)
  ) {
    return "active";
  }

  return "pending";
}

function getPointClass(type) {
  const clean = String(type || "").toUpperCase();

  if (clean === "PICKUP") return "pickup";
  if (clean === "DELIVERY") return "delivery";
  if (clean === "STRATEGICPOINT") return "strategic";

  return "default";
}

function buildAddress(point) {
  const parts = [
    point?.address,
    point?.postalCode,
    point?.city,
    point?.country,
  ]
    .map((x) => String(x || "").trim())
    .filter(Boolean);

  return parts.join(", ");
}

function coordinateGroupKey(coords) {
  if (!coords) return "missing";

  // 5 decimals ≈ 1m. Good enough to detect same-location markers.
  return `${Number(coords.lat).toFixed(5)},${Number(coords.lng).toFixed(5)}`;
}

function buildMarkerGroups(points) {
  const groups = new Map();

  points.forEach((point) => {
    if (!point.mapCoords) return;

    const key = coordinateGroupKey(point.mapCoords);

    if (!groups.has(key)) {
      groups.set(key, {
        key,
        coords: point.mapCoords,
        points: [],
      });
    }

    groups.get(key).points.push(point);
  });

  return Array.from(groups.values()).map((group) => {
    const statusPriority = [
      "delayed",
      "active",
      "pending",
      "completed",
      "cancelled",
    ];

    const markerClass =
      statusPriority.find((statusClass) =>
        group.points.some(
          (point) => point.mapStatusClass === statusClass
        )
      ) || "pending";

    return {
      ...group,
      markerClass,
      label:
        group.points.length === 1
          ? String(group.points[0].mapOrderNumber)
          : group.points.map((point) => point.mapOrderNumber).join("+"),
    };
  });
}

function buildDirectRouteGeoJsonFromRoutingLocations(locations) {
  return {
    type: "Feature",
    geometry: {
      type: "LineString",
      coordinates: locations.map((location) => [location.lon, location.lat]),
    },
    properties: {
      source: "DIRECT_FALLBACK",
    },
  };
}

function buildRoutingLocations(validPoints, manualViaPoints, viaTargetIndex) {
  if (!validPoints.length) return [];

  const maxTargetIndex = Math.max(validPoints.length - 2, 0);
  const safeTargetIndex = Math.min(
    Math.max(Number(viaTargetIndex) || 0, 0),
    maxTargetIndex
  );

  const routeLocations = [];

  validPoints.forEach((point, index) => {
    routeLocations.push({
      id: point.id,
      type: point.type,
      sequence: point.sequence,
      lat: point.mapCoords.lat,
      lon: point.mapCoords.lng,
      source: "SESSION_POINT",
    });

    const segmentViaPoints = manualViaPoints
      .filter((viaPoint) => {
        const rawSegmentIndex = Number(viaPoint?.segmentIndex);

        const pointSegmentIndex = Number.isFinite(rawSegmentIndex)
          ? Math.min(Math.max(rawSegmentIndex, 0), maxTargetIndex)
          : safeTargetIndex;

        return pointSegmentIndex === index;
      })
      .sort((a, b) => {
        return (Number(a?.order) || 0) - (Number(b?.order) || 0);
      });

    segmentViaPoints.forEach((viaPoint, viaIndex) => {
      routeLocations.push({
        id: viaPoint.id || `via-${index}-${viaIndex}`,
        type: "VIA_POINT",
        sequence: `via-${index + 1}-${viaIndex + 1}`,
        lat: viaPoint.lat,
        lon: viaPoint.lng,
        source: "MANUAL_VIA_POINT",
      });
    });
  });

  return routeLocations;
}

function buildMarkerFeatureCollection(markerGroups, t) {
  return {
    type: "FeatureCollection",
    features: markerGroups.map((group) => ({
      type: "Feature",
      geometry: {
        type: "Point",
        coordinates: [group.coords.lng, group.coords.lat],
      },
      properties: {
        key: group.key,
        label: group.label,
        markerClass: group.markerClass,
        groupSize: group.points.length,
        popupHtml: buildPointPopupHtml(group, t),
      },
    })),
  };
}

function formatKm(value, locale) {
  if (value === undefined || value === null || value === "") return null;

  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return null;

  const formatted = new Intl.NumberFormat(locale || "pt-PT", {
    minimumFractionDigits: n >= 100 ? 0 : 1,
    maximumFractionDigits: n >= 100 ? 0 : 1,
  }).format(n);

  return `${formatted} km`;
}

function formatDuration(seconds, t) {
  const n = Number(seconds);
  if (!Number.isFinite(n) || n <= 0) return null;

  const minutes = Math.round(n / 60);
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;

  if (h <= 0) return t("format.minutes", { minutes: m });
  if (m <= 0) return t("format.hours", { hours: h });

  return t("format.hoursMinutes", { hours: h, minutes: m });
}

function formatCurrency(value, currency = "EUR", locale = "pt-PT") {
  const number = Number(value);

  if (!Number.isFinite(number)) return null;

  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(number);
}

function getRoutePrimaryRoadName(route) {
  const rawRoadNames = [
    route?.routeName,
    route?.route_name,
    route?.mainRoad,
    route?.main_road,
    route?.roadName,
    route?.road_name,
    route?.primaryRoad,
    route?.primary_road,
    route?.summary?.routeName,
    route?.summary?.route_name,
    route?.summary?.mainRoad,
    route?.summary?.main_road,
    route?.summary?.roadName,
    route?.summary?.road_name,
    Array.isArray(route?.roadNames) ? route.roadNames[0] : null,
    Array.isArray(route?.road_names) ? route.road_names[0] : null,
  ];

  const cleanRoadName = rawRoadNames
    .map((value) => String(value || "").trim())
    .find(Boolean);

  return cleanRoadName || null;
}

function getRouteDisplayName(route, index, t) {
  const rawLabel = String(route?.label || "").trim();
  const roadName = getRoutePrimaryRoadName(route);

  if (roadName) {
    if (/^(por|via|par|durch)\s+/i.test(roadName)) return roadName;

    return t("routes.viaRoad", { road: roadName });
  }

  if (/^(por|via)\s+/i.test(rawLabel)) return rawLabel;

  if (index === 0) return t("routes.best");

  return t("routes.alternative", { index });
}

function isTrueRouteFlag(value) {
  if (value === true || value === 1) return true;

  if (typeof value === "string") {
    const clean = value.trim().toLowerCase();
    return clean === "true" || clean === "yes" || clean === "1";
  }

  return false;
}

function routeHasTolls(route) {
  return [
    route?.hasTolls,
    route?.has_tolls,
    route?.hasToll,
    route?.has_toll,
    route?.toll,
    route?.tolls,
    route?.summary?.hasTolls,
    route?.summary?.has_tolls,
    route?.summary?.hasToll,
    route?.summary?.has_toll,
    route?.summary?.toll,
    route?.summary?.tolls,
  ].some(isTrueRouteFlag);
}

function routeUsesHighways(route) {
  return [
    route?.hasHighways,
    route?.has_highways,
    route?.hasHighway,
    route?.has_highway,
    route?.summary?.hasHighways,
    route?.summary?.has_highways,
    route?.summary?.hasHighway,
    route?.summary?.has_highway,
  ].some(isTrueRouteFlag);
}

function getRouteRoadLabels(route) {
  if (!route || route?.source === "DIRECT_FALLBACK") return [];

  const rawRoadNames = [
    route?.routeName,
    route?.route_name,
    ...(Array.isArray(route?.roadNames) ? route.roadNames : []),
    ...(Array.isArray(route?.road_names) ? route.road_names : []),
  ];

  const seen = new Set();

  const uniqueRoadNames = rawRoadNames
    .map((value) =>
      String(value || "")
        .trim()
        .replace(/\s+/g, " ")
    )
    .filter((value) => {
      if (!value) return false;
      if (value === "[object Object]") return false;

      const lower = value.toLowerCase();

      if (
        lower === "true" ||
        lower === "false" ||
        lower === "unknown" ||
        lower === "unnamed road"
      ) {
        return false;
      }

      if (seen.has(lower)) return false;

      seen.add(lower);
      return true;
    });

  const looksLikeRoadReference = (value) =>
    /(^|\b)(A|IP|IC|EN|N|M|EM|ER|CM)\s*-?\s*\d+[A-Z]?(\b|$)/i.test(value);

  return uniqueRoadNames
    .sort((a, b) => {
      return Number(looksLikeRoadReference(b)) - Number(looksLikeRoadReference(a));
    })
    .slice(0, 4);
}

function getRouteWarningLabel(route, avoidTolls = false, t) {
  if (avoidTolls && routeHasTolls(route)) {
    return t("routes.tolls.couldNotAvoidAll");
  }

  return null;
}

function getRouteNoTrafficLabel(route, t) {
  const noTrafficSeconds =
    route?.durationNoTrafficSeconds ??
    route?.duration_no_traffic_seconds ??
    route?.durationWithoutTrafficSeconds ??
    route?.duration_without_traffic_seconds ??
    route?.summary?.durationNoTrafficSeconds ??
    route?.summary?.duration_no_traffic_seconds ??
    null;

  const formatted = formatDuration(noTrafficSeconds, t);

  return formatted
    ? t("routes.noTraffic", { duration: formatted })
    : null;
}

function getRouteOptionClassName(index, selectedRouteIndex) {
  return [
    "ts-map-route-option",
    index === selectedRouteIndex ? "is-active" : "",
  ]
    .filter(Boolean)
    .join(" ");
}

function normalizeRouteResponse(data, routingLocations, t) {
  const routes = Array.isArray(data?.routes) ? data.routes : [];

  if (routes.length) {
    return routes
      .map((route, index) => {
        const geometry =
          route.geometry?.type === "Feature"
            ? route.geometry
            : route.geometry?.type === "LineString"
              ? {
                  type: "Feature",
                  geometry: route.geometry,
                  properties: {},
                }
              : null;

        if (!geometry) return null;

        return {
          id: route.id || `route-${index}`,
          label:
            route.label ||
            (index === 0
              ? t("routes.main")
              : t("routes.alternative", { index })),

          routeName:
            route.routeName ??
            route.route_name ??
            route.mainRoad ??
            route.main_road ??
            route.roadName ??
            route.road_name ??
            route.primaryRoad ??
            route.primary_road ??
            route.summary?.routeName ??
            route.summary?.route_name ??
            route.summary?.mainRoad ??
            route.summary?.main_road ??
            route.summary?.roadName ??
            route.summary?.road_name ??
            null,

          roadNames:
            route.roadNames ??
            route.road_names ??
            route.summary?.roadNames ??
            route.summary?.road_names ??
            [],

          hasTolls:
            route.hasTolls ??
            route.has_tolls ??
            route.hasToll ??
            route.has_toll ??
            route.toll ??
            route.tolls ??
            route.summary?.hasTolls ??
            route.summary?.has_tolls ??
            route.summary?.hasToll ??
            route.summary?.has_toll ??
            route.summary?.toll ??
            route.summary?.tolls ??
            false,

          tollText:
            route.tollText ??
            route.toll_text ??
            route.summary?.tollText ??
            route.summary?.toll_text ??
            null,

          tollDistanceKm:
            route.tollDistanceKm ??
            route.toll_distance_km ??
            null,

          tollRoadNames:
            route.tollRoadNames ??
            route.toll_road_names ??
            [],

          tollAnalysisStatus:
            route.tollAnalysisStatus ??
            route.toll_analysis_status ??
            null,

          tollEstimate:
            route.tollEstimate ??
            route.toll_estimate ??
            null,

          hasHighways:
            route.hasHighways ??
            route.has_highways ??
            route.hasHighway ??
            route.has_highway ??
            route.summary?.hasHighways ??
            route.summary?.has_highways ??
            route.summary?.hasHighway ??
            route.summary?.has_highway ??
            false,

          durationNoTrafficSeconds:
            route.durationNoTrafficSeconds ??
            route.duration_no_traffic_seconds ??
            route.durationWithoutTrafficSeconds ??
            route.duration_without_traffic_seconds ??
            route.summary?.durationNoTrafficSeconds ??
            route.summary?.duration_no_traffic_seconds ??
            null,

          distanceKm: route.distanceKm ?? route.distance_km ?? null,
          durationSeconds:
            route.durationSeconds ?? route.duration_seconds ?? null,
          geometry,
          source: route.source || data.provider || "VALHALLA",
        };
      })
      .filter(Boolean);
  }

  return [
    {
      id: "direct-fallback",
      label: t("routes.direct"),
      distanceKm: null,
      durationSeconds: null,
      geometry: buildDirectRouteGeoJsonFromRoutingLocations(routingLocations),
      source: "DIRECT_FALLBACK",
    },
  ];
}

function getLineStringCoordinates(routeOrGeometry) {
  const geometry =
    routeOrGeometry?.geometry?.type === "LineString"
      ? routeOrGeometry.geometry
      : routeOrGeometry?.geometry?.geometry?.type === "LineString"
        ? routeOrGeometry.geometry.geometry
        : null;

  return Array.isArray(geometry?.coordinates) ? geometry.coordinates : [];
}

function sameCoordinate(a, b) {
  if (!Array.isArray(a) || !Array.isArray(b)) return false;

  return (
    Math.abs(Number(a[0]) - Number(b[0])) < 0.000001 &&
    Math.abs(Number(a[1]) - Number(b[1])) < 0.000001
  );
}

function mergeRouteGeometries(routes) {
  const coordinates = [];

  routes.forEach((route, routeIndex) => {
    const segmentCoordinates = getLineStringCoordinates(route);

    segmentCoordinates.forEach((coordinate, coordinateIndex) => {
      if (
        routeIndex > 0 &&
        coordinateIndex === 0 &&
        sameCoordinate(coordinates[coordinates.length - 1], coordinate)
      ) {
        return;
      }

      coordinates.push(coordinate);
    });
  });

  return {
    type: "Feature",
    geometry: {
      type: "LineString",
      coordinates,
    },
    properties: {
      source: "COMPOSED_ROUTE",
    },
  };
}

function sumRouteNumber(routes, key) {
  const values = routes
    .map((route) => Number(route?.[key]))
    .filter((value) => Number.isFinite(value) && value > 0);

  if (values.length !== routes.length) return null;

  return values.reduce((sum, value) => sum + value, 0);
}

function mergeRouteRoadNames(routes) {
  const seen = new Set();
  const roadNames = [];

  routes.forEach((route) => {
    const values = [
      route?.routeName,
      ...(Array.isArray(route?.roadNames) ? route.roadNames : []),
    ];

    values.forEach((value) => {
      const clean = String(value || "")
        .trim()
        .replace(/\s+/g, " ");

      if (!clean || clean === "[object Object]") return;

      const lower = clean.toLowerCase();

      if (seen.has(lower)) return;

      seen.add(lower);
      roadNames.push(clean);
    });
  });

  return roadNames;
}

function mergeRouteTollRoadNames(routes) {
  const seen = new Set();
  const names = [];

  routes.forEach((route) => {
    const values = Array.isArray(route?.tollRoadNames)
      ? route.tollRoadNames
      : [];

    values.forEach((value) => {
      const clean = String(value || "").trim();

      if (!clean) return;

      const lower = clean.toLowerCase();

      if (seen.has(lower)) return;

      seen.add(lower);
      names.push(clean);
    });
  });

  return names;
}

function sumRouteTollDistance(routes) {
  const tolledRoutes = routes.filter((route) => routeHasTolls(route));

  if (!tolledRoutes.length) return 0;

  const values = tolledRoutes.map((route) =>
    Number(route?.tollDistanceKm)
  );

  if (values.some((value) => !Number.isFinite(value))) {
    return null;
  }

  return values.reduce((total, value) => total + value, 0);
}

function mergeRouteTollEstimate(routes) {
  const tolledRoutes = routes.filter((route) => routeHasTolls(route));

  if (!tolledRoutes.length) return null;

  const estimates = tolledRoutes.map((route) => route?.tollEstimate);

  if (
    estimates.some(
      (estimate) =>
        !estimate ||
        !Number.isFinite(Number(estimate?.amount)) ||
        !Number.isFinite(Number(estimate?.minimum)) ||
        !Number.isFinite(Number(estimate?.maximum))
    )
  ) {
    return null;
  }

  return {
    currency: estimates[0]?.currency || "EUR",

    amount: estimates.reduce(
      (total, estimate) => total + Number(estimate.amount),
      0
    ),

    minimum: estimates.reduce(
      (total, estimate) => total + Number(estimate.minimum),
      0
    ),

    maximum: estimates.reduce(
      (total, estimate) => total + Number(estimate.maximum),
      0
    ),

    vehicleClass: estimates[0]?.vehicleClass || 4,
    country: estimates[0]?.country || "PT",
    modelVersion: "PT_AVERAGE_2026_V1",
    confidence: "MEDIUM",
    method: "COMPOSED_TOLL_EDGE_DISTANCE_AVERAGE",
  };
}

function getRouteLabelCoordinate(route, ratio = 0.5) {
  const coordinates = getLineStringCoordinates(route);

  if (!coordinates.length) return null;

  const safeRatio = Math.min(Math.max(Number(ratio) || 0.5, 0.18), 0.82);
  const index = Math.min(
    Math.max(Math.floor(coordinates.length * safeRatio), 0),
    coordinates.length - 1
  );

  return coordinates[index];
}

function getPixelDistance(a, b) {
  if (!a || !b) return Number.POSITIVE_INFINITY;
  return Math.hypot(a.x - b.x, a.y - b.y);
}

function getPointToSegmentDistance(point, start, end) {
  if (!point || !start || !end) return Number.POSITIVE_INFINITY;

  const dx = end.x - start.x;
  const dy = end.y - start.y;

  if (dx === 0 && dy === 0) {
    return getPixelDistance(point, start);
  }

  const t = Math.max(
    0,
    Math.min(
      1,
      ((point.x - start.x) * dx + (point.y - start.y) * dy) / (dx * dx + dy * dy)
    )
  );

  const projection = {
    x: start.x + t * dx,
    y: start.y + t * dy,
  };

  return getPixelDistance(point, projection);
}

function getNearestCoordinateOnRoute({
  map,
  route,
  lng,
  lat,
}) {
  const fallbackCoordinate = [lng, lat];
  const coordinates = getLineStringCoordinates(route);

  if (
    !map ||
    !Number.isFinite(Number(lng)) ||
    !Number.isFinite(Number(lat)) ||
    coordinates.length < 2
  ) {
    return fallbackCoordinate;
  }

  const targetPixel = map.project([lng, lat]);

  let bestPixel = null;
  let bestDistance = Number.POSITIVE_INFINITY;

  coordinates.slice(0, -1).forEach((startCoordinate, index) => {
    const endCoordinate = coordinates[index + 1];

    if (
      !Array.isArray(startCoordinate) ||
      startCoordinate.length < 2 ||
      !Array.isArray(endCoordinate) ||
      endCoordinate.length < 2
    ) {
      return;
    }

    const startPixel = map.project(startCoordinate);
    const endPixel = map.project(endCoordinate);

    const dx = endPixel.x - startPixel.x;
    const dy = endPixel.y - startPixel.y;
    const segmentLengthSquared = dx * dx + dy * dy;

    const ratio =
      segmentLengthSquared === 0
        ? 0
        : Math.max(
            0,
            Math.min(
              1,
              ((targetPixel.x - startPixel.x) * dx +
                (targetPixel.y - startPixel.y) * dy) /
                segmentLengthSquared
            )
          );

    const candidatePixel = {
      x: startPixel.x + ratio * dx,
      y: startPixel.y + ratio * dy,
    };

    const distance = getPixelDistance(
      targetPixel,
      candidatePixel
    );

    if (distance < bestDistance) {
      bestDistance = distance;
      bestPixel = candidatePixel;
    }
  });

  if (!bestPixel) {
    return fallbackCoordinate;
  }

  const snappedLngLat = map.unproject([
    bestPixel.x,
    bestPixel.y,
  ]);

  return [
    snappedLngLat.lng,
    snappedLngLat.lat,
  ];
}

function getNearestRouteSegmentIndex({ map, validPoints, lngLat }) {
  if (!map || !Array.isArray(validPoints) || validPoints.length < 2 || !lngLat) {
    return 0;
  }

  const targetPixel = map.project([lngLat.lng, lngLat.lat]);

  let best = {
    index: 0,
    distance: Number.POSITIVE_INFINITY,
  };

  validPoints.slice(0, -1).forEach((point, index) => {
    const nextPoint = validPoints[index + 1];

    if (!point?.mapCoords || !nextPoint?.mapCoords) return;

    const startPixel = map.project([point.mapCoords.lng, point.mapCoords.lat]);
    const endPixel = map.project([nextPoint.mapCoords.lng, nextPoint.mapCoords.lat]);

    const distance = getPointToSegmentDistance(targetPixel, startPixel, endPixel);

    if (distance < best.distance) {
      best = {
        index,
        distance,
      };
    }
  });

  return best.index;
}

function buildSnapshotViaPoints({
  snapshot,
  validPoints,
  map,
}) {
  const snapshotPoints =
    Array.isArray(snapshot?.points)
      ? snapshot.points
      : [];

  return snapshotPoints
    .filter((point) => {
      const type = String(
        point?.type || ""
      ).toUpperCase();

      return (
        type !== "ORIGIN" &&
        type !== "DESTINATION"
      );
    })
    .map((point, index) => {
      const lat = toNumber(
        point?.lat ??
        point?.latitude
      );

      const lng = toNumber(
        point?.lng ??
        point?.lon ??
        point?.longitude
      );

      if (
        lat === null ||
        lng === null
      ) {
        return null;
      }

      const segmentIndex =
        map &&
        validPoints.length >= 2
          ? getNearestRouteSegmentIndex(
              {
                map,
                validPoints,
                lngLat: {
                  lat,
                  lng,
                },
              }
            )
          : 0;

      return {
        id:
          `template-via-${snapshot?.templateId || "route"}-${point?.sequence || index + 1}`,

        lat,
        lng,
        segmentIndex,
        order:
          Number(point?.sequence) ||
          index + 1,

        source:
          "ROUTE_TEMPLATE",

        templatePointId:
          point?.templatePointId ||
          null,

        name:
          point?.name || null,

        address:
          point?.address || null,
      };
    })
    .filter(Boolean);
}

function buildRouteDragMarkerElement() {
  const markerEl = document.createElement("div");
  markerEl.className = "ts-map-route-drag-marker";
  markerEl.innerHTML = "<span></span>";

  return markerEl;
}

function pickRouteLabelCoordinate({
  route,
  map,
  blockedCoordinates = [],
  usedLabelPixelPositions = [],
}) {
  if (!map) return getRouteLabelCoordinate(route, 0.5);

  const candidateRatios = [0.5, 0.34, 0.66, 0.24, 0.76, 0.14, 0.86];

  const blockedPixels = blockedCoordinates
    .filter((coord) => Array.isArray(coord) && coord.length >= 2)
    .map((coord) => map.project(coord));

  let bestCandidate = null;

  candidateRatios.forEach((ratio, index) => {
    const coord = getRouteLabelCoordinate(route, ratio);
    if (!coord) return;

    const pixel = map.project(coord);

    const minPointDistance = blockedPixels.length
      ? Math.min(
          ...blockedPixels.map((blockedPixel) =>
            getPixelDistance(pixel, blockedPixel)
          )
        )
      : Number.POSITIVE_INFINITY;

    const minLabelDistance = usedLabelPixelPositions.length
      ? Math.min(
          ...usedLabelPixelPositions.map((usedPixel) =>
            getPixelDistance(pixel, usedPixel)
          )
        )
      : Number.POSITIVE_INFINITY;

    const tooCloseToPoint = minPointDistance < 86;
    const tooCloseToLabel = minLabelDistance < 118;

    const score =
      Math.min(minPointDistance, 9999) +
      Math.min(minLabelDistance, 9999) * 0.7 -
      index * 6;

    const candidate = {
      coord,
      pixel,
      score,
      tooCloseToPoint,
      tooCloseToLabel,
    };

    if (!bestCandidate) {
      bestCandidate = candidate;
      return;
    }

    const currentIsValid =
      !bestCandidate.tooCloseToPoint && !bestCandidate.tooCloseToLabel;
    const nextIsValid = !tooCloseToPoint && !tooCloseToLabel;

    if (nextIsValid && !currentIsValid) {
      bestCandidate = candidate;
      return;
    }

    if (nextIsValid === currentIsValid && score > bestCandidate.score) {
      bestCandidate = candidate;
    }
  });

  return bestCandidate?.coord || getRouteLabelCoordinate(route, 0.5);
}

function locationsFromValidPoints(validPoints) {
  return validPoints.map((point) => ({
    id: point.id,
    type: point.type,
    sequence: point.sequence,
    lat: point.mapCoords.lat,
    lon: point.mapCoords.lng,
  }));
}

async function requestRoute({
  transportsessionId,
  locations,
  alternatives,
  avoidTolls = false,
  includeTollEstimate = true,
  t,
}) {
  const response = await axios.post("/maps/route", {
    transportsessionId,
    costing: "truck",
    alternatives,
    avoidTolls,
    includeTollEstimate,
    tollVehicleClass: 4,
    locations: locations.map((location) => ({
      id: location.id,
      type: location.type,
      sequence: location.sequence,
      lat: location.lat,
      lon: location.lon,
    })),
  });

  return normalizeRouteResponse(response.data, locations, t);
}

async function fetchPremiumRouteOptions({
  transportsessionId,
  validPoints,
  manualViaPoints,
  viaTargetIndex,
  routingLocations,
  avoidTolls,
  t,
}) {
  if (routingLocations.length < 2) return [];

  if (validPoints.length <= 2 || manualViaPoints.length > 0) {
    return requestRoute({
      transportsessionId,
      locations: routingLocations,
      alternatives:
        validPoints.length === 2 && manualViaPoints.length === 0 ? 3 : 0,
      avoidTolls,
      t,
    });
  }

  const pointLocations = locationsFromValidPoints(validPoints);
  const safeSegmentIndex = Math.min(
    Math.max(Number(viaTargetIndex) || 0, 0),
    Math.max(pointLocations.length - 2, 0)
  );

  const segmentResponses = await Promise.all(
    pointLocations.slice(0, -1).map((location, index) => {
      const nextLocation = pointLocations[index + 1];

      return requestRoute({
        transportsessionId,
        locations: [location, nextLocation],
        alternatives: index === safeSegmentIndex ? 3 : 0,
        avoidTolls,
        t,
      });
    })
  );

  const selectedSegmentRoutes = segmentResponses[safeSegmentIndex] || [];
  const selectedSegmentLabel = `${validPoints[safeSegmentIndex].mapOrderNumber} → ${
    validPoints[safeSegmentIndex + 1].mapOrderNumber
  }`;

  return selectedSegmentRoutes
    .map((selectedSegmentRoute, routeIndex) => {
      const chosenSegments = segmentResponses.map((segmentRoutes, segmentIndex) => {
        if (segmentIndex === safeSegmentIndex) return selectedSegmentRoute;
        return segmentRoutes[0];
      });

      if (chosenSegments.some((route) => !route?.geometry)) return null;

      return {
        id: `composed-${safeSegmentIndex}-${routeIndex}`,
        label:
          routeIndex === 0
            ? t("routes.main")
            : t("routes.alternative", { index: routeIndex }),
        routeName:
          selectedSegmentRoute?.routeName ||
          selectedSegmentRoute?.mainRoad ||
          selectedSegmentRoute?.roadName ||
          null,
        roadNames: mergeRouteRoadNames(chosenSegments),
        hasTolls: chosenSegments.some((route) => routeHasTolls(route)),
        hasHighways: chosenSegments.some((route) => routeUsesHighways(route)),
        tollDistanceKm: sumRouteTollDistance(chosenSegments),
        tollRoadNames: mergeRouteTollRoadNames(chosenSegments),
        tollEstimate: mergeRouteTollEstimate(chosenSegments),
        tollAnalysisStatus: chosenSegments.every(
          (route) =>
            !routeHasTolls(route) ||
            route?.tollAnalysisStatus === "ESTIMATED"
        )
          ? "ESTIMATED"
          : "PARTIALLY_UNAVAILABLE",
        tollText:
          chosenSegments.find((route) => route?.tollText || route?.toll_text)
            ?.tollText ||
          chosenSegments.find((route) => route?.tollText || route?.toll_text)
            ?.toll_text ||
          null,
        durationNoTrafficSeconds: sumRouteNumber(
          chosenSegments,
          "durationNoTrafficSeconds"
        ),
        segmentLabel: t("routes.segment", {
          segment: selectedSegmentLabel,
        }),
        distanceKm: sumRouteNumber(chosenSegments, "distanceKm"),
        durationSeconds: sumRouteNumber(chosenSegments, "durationSeconds"),
        geometry: mergeRouteGeometries(chosenSegments),
        source: "VALHALLA",
      };
    })
    .filter(Boolean);
}

function buildPointPopupHtml(group, t) {
  if (!group?.points?.length) return "";

  if (group.points.length === 1) {
    const point = group.points[0];

    return `
      <div class="ts-map-popup">
        <strong>${escapeHtml(point.mapOrderNumber)}. ${escapeHtml(point.mapTypeLabel)}</strong>
        <span>${escapeHtml(point.location || t("points.noLocation"))}</span>
        ${
          point.mapAddress
            ? `<small>${escapeHtml(point.mapAddress)}</small>`
            : ""
        }
        ${
          point.date || point.hour
            ? `<small>${escapeHtml([point.date, point.hour].filter(Boolean).join(" · "))}</small>`
            : ""
        }
      </div>
    `;
  }

  return `
    <div class="ts-map-popup ts-map-popup-group">
      <strong>${escapeHtml(
        t("points.popup.pointsAtLocation", {
          count: group.points.length,
        })
      )}</strong>
      ${group.points
        .map((point) => {
          return `
            <div class="ts-map-popup-group-item">
              <b>${escapeHtml(point.mapOrderNumber)}. ${escapeHtml(point.mapTypeLabel)}</b>
              <span>${escapeHtml(point.location || t("points.noLocation"))}</span>
              ${
                point.mapAddress
                  ? `<small>${escapeHtml(point.mapAddress)}</small>`
                  : ""
              }
            </div>
          `;
        })
        .join("")}
    </div>
  `;
}

function removeLayerIfExists(map, layerId) {
  try {
    if (map?.getLayer?.(layerId)) {
      map.removeLayer(layerId);
    }
  } catch {}
}

function removeSourceIfExists(map, sourceId) {
  try {
    if (map?.getSource?.(sourceId)) {
      map.removeSource(sourceId);
    }
  } catch {}
}

function clearDynamicMapLayersAndSources(map) {
  if (!map?.getStyle) return;

  const style = map.getStyle();
  const layers = Array.isArray(style?.layers) ? style.layers : [];
  const sources = style?.sources || {};

  const fixedLayerIds = new Set([
    PREVIEW_ROUTE_LINE_LAYER_ID,
    PREVIEW_ROUTE_CASING_LAYER_ID,
    "transport-session-route-hitbox",
    "transport-session-route-line",
    "transport-session-route-casing",
    "transport-session-points-label",
    "transport-session-points-circle",
    "transport-session-points-halo",
  ]);

  [...layers]
    .reverse()
    .map((layer) => layer.id)
    .filter((layerId) => {
      return (
        fixedLayerIds.has(layerId) ||
        String(layerId).startsWith("transport-session-route-alt-")
      );
    })
    .forEach((layerId) => removeLayerIfExists(map, layerId));

  Object.keys(sources)
    .filter((sourceId) => {
      return (
        sourceId === PREVIEW_ROUTE_SOURCE_ID ||
        sourceId === "transport-session-route" ||
        sourceId === "transport-session-points" ||
        String(sourceId).startsWith("transport-session-route-alt-")
      );
    })
    .forEach((sourceId) => removeSourceIfExists(map, sourceId));
}

function addLayerWithOptionalBefore(map, layer, beforeLayerId) {
  if (beforeLayerId && map.getLayer(beforeLayerId)) {
    map.addLayer(layer, beforeLayerId);
    return;
  }

  map.addLayer(layer);
}

function clearRoutePreviewLayers(map) {
  removeLayerIfExists(map, PREVIEW_ROUTE_LINE_LAYER_ID);
  removeLayerIfExists(map, PREVIEW_ROUTE_CASING_LAYER_ID);
  removeSourceIfExists(map, PREVIEW_ROUTE_SOURCE_ID);
}

function getPreviewRouteBeforeLayerId(map) {
  if (map?.getLayer?.("transport-session-points-halo")) {
    return "transport-session-points-halo";
  }

  if (map?.getLayer?.("transport-session-route-hitbox")) {
    return "transport-session-route-hitbox";
  }

  return undefined;
}

function setRoutePreviewGeoJson(map, geoJson) {
  if (!map || !geoJson?.geometry) return;

  const existingSource = map.getSource(PREVIEW_ROUTE_SOURCE_ID);

  if (existingSource?.setData) {
    existingSource.setData(geoJson);
    return;
  }

  clearRoutePreviewLayers(map);

  map.addSource(PREVIEW_ROUTE_SOURCE_ID, {
    type: "geojson",
    data: geoJson,
  });

  const beforeLayerId = getPreviewRouteBeforeLayerId(map);

  addLayerWithOptionalBefore(
    map,
    {
      id: PREVIEW_ROUTE_CASING_LAYER_ID,
      type: "line",
      source: PREVIEW_ROUTE_SOURCE_ID,
      layout: {
        "line-cap": "round",
        "line-join": "round",
      },
      paint: {
        "line-color": "#ffffff",
        "line-width": 11,
        "line-opacity": 0.96,
      },
    },
    beforeLayerId
  );

  addLayerWithOptionalBefore(
    map,
    {
      id: PREVIEW_ROUTE_LINE_LAYER_ID,
      type: "line",
      source: PREVIEW_ROUTE_SOURCE_ID,
      layout: {
        "line-cap": "round",
        "line-join": "round",
      },
      paint: {
        "line-color": "#1a73e8",
        "line-width": 6,
        "line-opacity": 0.98,
      },
    },
    beforeLayerId
  );
}

function setRouteDraggingVisualState(map, isDragging) {
  if (!map?.getStyle) return;

  if (map.getLayer("transport-session-route-line")) {
    map.setPaintProperty(
      "transport-session-route-line",
      "line-opacity",
      isDragging ? 0.35 : 0.98
    );

    map.setPaintProperty(
      "transport-session-route-line",
      "line-width",
      isDragging ? 4 : 5
    );
  }

  if (map.getLayer("transport-session-route-casing")) {
    map.setPaintProperty(
      "transport-session-route-casing",
      "line-opacity",
      isDragging ? 0.45 : 0.95
    );
  }

  const style = map.getStyle();
  const layers = Array.isArray(style?.layers) ? style.layers : [];

  layers.forEach((layer) => {
    const layerId = String(layer.id || "");

    if (
      layerId.startsWith("transport-session-route-alt-") &&
      layerId.endsWith("-line") &&
      map.getLayer(layerId)
    ) {
      map.setPaintProperty(layerId, "line-opacity", isDragging ? 0.22 : 0.52);
    }

    if (
      layerId.startsWith("transport-session-route-alt-") &&
      layerId.endsWith("-casing") &&
      map.getLayer(layerId)
    ) {
      map.setPaintProperty(layerId, "line-opacity", isDragging ? 0.38 : 0.8);
    }
  });
}

export default function TransportSessionMapModal({
  isOpen,
  onClose,
  transportsessionId,
  points = [],
  onReorderPoints,
}) {
  const { t, i18n } = useTranslation("transport_session_map_modal");
  const locale = i18n.resolvedLanguage || i18n.language || "pt-PT";

  const hasRouteTemplateAccess =
    useMemo(
      () =>
        canManageRouteTemplates(),
      []
    );

  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const pointsListRef = useRef(null);
  const pointsSortableRef = useRef(null);
  const onReorderPointsRef = useRef(onReorderPoints);
  const manualViaPointsRef = useRef([]);
  const markersRef = useRef([]);
  const routeLabelMarkersRef = useRef([]);
  const routeDragMarkerRef = useRef(null);
  const routeDragStateRef = useRef(null);
  const routePreviewTimeoutRef = useRef(null);
  const routePreviewRequestSeqRef = useRef(0);
  const routePreviewPendingPointRef = useRef(null);
  const routePreviewLastRequestAtRef = useRef(0);
  const hasInitialFitRef = useRef(false);
  const routeSnapshotRestoreSignatureRef = useRef("");
  const automaticRouteSaveTimerRef = useRef(null);
  const automaticRouteSaveSignatureRef = useRef("");
  const automaticRouteSaveActionRef = useRef(null);

  const mapResizeTimerRef = useRef(null);
  const mapReadyFallbackTimerRef = useRef(null);

  const [mapReady, setMapReady] = useState(false);
  const [isMapVisuallyReady, setIsMapVisuallyReady] = useState(false);

  const [routeOptions, setRouteOptions] = useState([]);
  const [selectedRouteIndex, setSelectedRouteIndex] = useState(0);
  const [routeLoading, setRouteLoading] = useState(false);
  const [routeError, setRouteError] = useState(null);
  const [avoidTolls, setAvoidTolls] = useState(false);

  const [viaPickMode, setViaPickMode] = useState(false);
  const [manualViaPoints, setManualViaPoints] = useState([]);
  const [viaTargetIndex, setViaTargetIndex] = useState(0);
  const [isPointsPanelCollapsed, setIsPointsPanelCollapsed] = useState(false);
  const [isReorderingPoints, setIsReorderingPoints] = useState(false);
  const [pointsReorderError, setPointsReorderError] = useState(null);

  const [
    appliedRouteSnapshot,
    setAppliedRouteSnapshot,
  ] = useState(null);

  const [
    isSavedRoutesOpen,
    setIsSavedRoutesOpen,
  ] = useState(false);

  const [
    savedRoutes,
    setSavedRoutes,
  ] = useState([]);

  const [
    savedRoutesSearch,
    setSavedRoutesSearch,
  ] = useState("");

  const [
    savedRoutesLoading,
    setSavedRoutesLoading,
  ] = useState(false);

  const [
    savedRoutesError,
    setSavedRoutesError,
  ] = useState(null);

  const [
    applyingRouteTemplateId,
    setApplyingRouteTemplateId,
  ] = useState(null);

  const [
    removingRouteTemplate,
    setRemovingRouteTemplate,
  ] = useState(false);

  const [
    savingSessionRoute,
    setSavingSessionRoute,
  ] = useState(false);

  const [
    sessionRouteSaveError,
    setSessionRouteSaveError,
  ] = useState(null);

  useEffect(() => {
    onReorderPointsRef.current = onReorderPoints;
  }, [onReorderPoints]);

  useEffect(() => {
    manualViaPointsRef.current = manualViaPoints;
  }, [manualViaPoints]);

  const mappedPoints = useMemo(() => {
    return (Array.isArray(points) ? points : [])
      .map((point, rawIndex) => {
        const coords = parseCoordinates(point);

        return {
          ...point,
          mapKey:
            point?.id != null
              ? `pd-${point.id}`
              : `no-id-${point?.type ?? "point"}-${rawIndex}`,
          mapCoords: coords,
          mapTypeLabel: getPointTypeLabel(point?.type, t),
          mapTypeClass: getPointClass(point?.type),
          mapStatus: getEffectivePointStatus(point),
          mapStatusClass: getPointStatusClass(point),
          mapAddress: buildAddress(point),
        };
      })
      .sort((a, b) => {
        const seqA = Number(a?.sequence);
        const seqB = Number(b?.sequence);

        if (Number.isFinite(seqA) && Number.isFinite(seqB) && seqA !== seqB) {
          return seqA - seqB;
        }

        const dateA = `${a?.date || "9999-12-31"}T${a?.hour || "23:59"}`;
        const dateB = `${b?.date || "9999-12-31"}T${b?.hour || "23:59"}`;

        return dateA.localeCompare(dateB);
      })
      .map((point, index) => ({
        ...point,
        mapOrderNumber: index + 1,
      }));
  }, [points, t]);

  const validPoints = useMemo(() => {
    return mappedPoints.filter((point) => point.mapCoords);
  }, [mappedPoints]);

  const markerGroups = useMemo(() => {
    return buildMarkerGroups(validPoints);
  }, [validPoints]);

  const routingLocations = useMemo(() => {
    return buildRoutingLocations(validPoints, manualViaPoints, viaTargetIndex);
  }, [validPoints, manualViaPoints, viaTargetIndex]);

  const segmentOptions = useMemo(() => {
    if (validPoints.length < 2) return [];

    return validPoints.slice(0, -1).map((point, index) => {
      const nextPoint = validPoints[index + 1];

      return {
        value: index,
        label: `${point.mapOrderNumber} → ${nextPoint.mapOrderNumber}`,
      };
    });
  }, [validPoints]);

  const filteredSavedRoutes =
    useMemo(() => {
      const search =
        savedRoutesSearch
          .trim()
          .toLowerCase();

      if (!search) {
        return savedRoutes;
      }

      return savedRoutes.filter(
        (route) => {
          return [
            route?.name,
            route?.description,
            route?.routeNumber,
          ]
            .map((value) =>
              String(value || "")
                .toLowerCase()
            )
            .some((value) =>
              value.includes(search)
            );
        }
      );
    }, [
      savedRoutes,
      savedRoutesSearch,
    ]);

  const hasManualRouteAdjustments =
    useMemo(() => {
      return manualViaPoints.some(
        (point) =>
          point?.source !==
          "ROUTE_TEMPLATE"
      );
    }, [
      manualViaPoints,
    ]);

  const missingCoordinatePoints = mappedPoints.length - validPoints.length;
  const selectedRoute = routeOptions[selectedRouteIndex] || null;
  const mappedPointIdsSignature = mappedPoints
    .map((point) => String(point?.id ?? ""))
    .join("|");

  const handleClose = useCallback(
    (event) => {
      event?.preventDefault?.();
      event?.stopPropagation?.();

      onClose?.();
    },
    [onClose]
  );

  const handleBackdropMouseDown = useCallback(
    (event) => {
      // Só fecha quando o clique foi realmente no backdrop.
      if (event.target !== event.currentTarget) return;

      handleClose(event);
    },
    [handleClose]
  );

  useEffect(() => {
    if (!isOpen) return undefined;

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        handleClose(event);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    document.body.classList.add("ts-map-modal-open");

    return () => {
      window.removeEventListener("keydown", onKeyDown);
      document.body.classList.remove("ts-map-modal-open");
    };
  }, [isOpen, handleClose]);

  useEffect(() => {
    if (!isOpen) {
      setRouteOptions([]);
      setSelectedRouteIndex(0);
      setRouteError(null);
      setRouteLoading(false);
      setAvoidTolls(false);
      setViaPickMode(false);
      setManualViaPoints([]);
      setViaTargetIndex(0);
      setIsPointsPanelCollapsed(false);
      setIsReorderingPoints(false);
      setPointsReorderError(null);

      setAppliedRouteSnapshot(null);
      setIsSavedRoutesOpen(false);
      setSavedRoutes([]);
      setSavedRoutesSearch("");
      setSavedRoutesLoading(false);
      setSavedRoutesError(null);
      setApplyingRouteTemplateId(null);
      setRemovingRouteTemplate(false);

      setSavingSessionRoute(false);
      setSessionRouteSaveError(null);

      if (
        automaticRouteSaveTimerRef.current
      ) {
        window.clearTimeout(
          automaticRouteSaveTimerRef.current
        );

        automaticRouteSaveTimerRef.current =
          null;
      }

      automaticRouteSaveSignatureRef.current =
        "";

      routeSnapshotRestoreSignatureRef.current = "";

      setMapReady(false);
      setIsMapVisuallyReady(false);
      hasInitialFitRef.current = false;
      return;
    }

    if (viaTargetIndex > Math.max(validPoints.length - 2, 0)) {
      setViaTargetIndex(0);
    }
  }, [
    isOpen,
    validPoints.length,
    viaTargetIndex,
  ]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setSessionRouteSaveError(null);
  }, [
    avoidTolls,
    isOpen,
    manualViaPoints,
    selectedRouteIndex,
  ]);

  useEffect(() => {
    if (
      !isOpen ||
      !transportsessionId
    ) {
      return undefined;
    }

    let cancelled = false;

    async function loadSessionRouteSnapshot() {
      try {
        const { data } =
          await axios.get(
            `/transportsessions/${transportsessionId}`
          );

        if (cancelled) return;

        const transportSession =
          data?.transportSession ??
          data;

        const snapshot =
          transportSession?.routeSnapshot ||
          null;

        setAppliedRouteSnapshot(
          snapshot
        );

        routeSnapshotRestoreSignatureRef.current =
          "";

        if (snapshot) {
          setAvoidTolls(
            Boolean(
              snapshot.avoidTolls
            )
          );
        }
      } catch (error) {
        if (cancelled) return;

        console.error(
          "[TransportSessionMapModal] Failed to load saved route:",
          error
        );

        setSavedRoutesError(
          t(
            "savedRoutes.errors.session"
          )
        );
      }
    }

    loadSessionRouteSnapshot();

    return () => {
      cancelled = true;
    };
  }, [
    isOpen,
    transportsessionId,
    t,
  ]);

  useEffect(() => {
    if (
      !isOpen ||
      !mapReady ||
      !mapRef.current ||
      !appliedRouteSnapshot
    ) {
      return;
    }

    const signature = [
      appliedRouteSnapshot
        ?.templateId,
      appliedRouteSnapshot
        ?.templateVersion,
      appliedRouteSnapshot
        ?.appliedAt,
    ].join(":");

    if (
      routeSnapshotRestoreSignatureRef
        .current === signature
    ) {
      return;
    }

    const restoredViaPoints =
      buildSnapshotViaPoints({
        snapshot:
          appliedRouteSnapshot,

        validPoints,

        map:
          mapRef.current,
      });

    setManualViaPoints(
      restoredViaPoints
    );

    setViaTargetIndex(
      restoredViaPoints[0]
        ?.segmentIndex || 0
    );

    setAvoidTolls(
      Boolean(
        appliedRouteSnapshot
          ?.avoidTolls
      )
    );

    routeSnapshotRestoreSignatureRef.current =
      signature;
  }, [
    appliedRouteSnapshot,
    isOpen,
    mapReady,
    validPoints,
  ]);

  useEffect(() => {
    if (
      !isOpen ||
      !isSavedRoutesOpen ||
      !hasRouteTemplateAccess
    ) {
      return undefined;
    }

    let cancelled = false;

    async function loadSavedRoutes() {
      setSavedRoutesLoading(true);
      setSavedRoutesError(null);

      try {
        const { data } =
          await axios.get(
            "/route-templates"
          );

        if (cancelled) return;

        setSavedRoutes(
          Array.isArray(data?.items)
            ? data.items
            : Array.isArray(data)
              ? data
              : []
        );
      } catch (error) {
        if (cancelled) return;

        console.error(
          "[TransportSessionMapModal] Failed to load route templates:",
          error
        );

        setSavedRoutesError(
          t(
            "savedRoutes.errors.load"
          )
        );
      } finally {
        if (!cancelled) {
          setSavedRoutesLoading(false);
        }
      }
    }

    loadSavedRoutes();

    return () => {
      cancelled = true;
    };
  }, [
    hasRouteTemplateAccess,
    isOpen,
    isSavedRoutesOpen,
    t,
  ]);

  useEffect(() => {
    if (pointsSortableRef.current) {
      pointsSortableRef.current.destroy();
      pointsSortableRef.current = null;
    }

    if (
      !isOpen ||
      isPointsPanelCollapsed ||
      !pointsListRef.current ||
      mappedPoints.length < 2 ||
      typeof onReorderPointsRef.current !== "function"
    ) {
      return undefined;
    }

    const sortable = Sortable.create(pointsListRef.current, {
      animation: 170,
      handle: ".ts-map-point-drag-handle",
      draggable: ".ts-map-point-card",
      filter: ".is-reorder-locked",
      preventOnFilter: true,
      dataIdAttr: "data-id",
      ghostClass: "ts-map-point-sortable-ghost",
      chosenClass: "ts-map-point-sortable-chosen",
      dragClass: "ts-map-point-sortable-drag",
      swapThreshold: 0.65,
      onStart: () => {
        setPointsReorderError(null);
      },
      onEnd: async (event) => {
        const previousIds = mappedPointIdsSignature
          .split("|")
          .filter(Boolean);

        const restorePreviousOrder = () => {
          try {
            (pointsSortableRef.current || sortable)?.sort(previousIds);
          } catch {}
        };

        const orderedIds = Array.from(
          pointsListRef.current?.querySelectorAll(".ts-map-point-card") || []
        )
          .map((element) => element.getAttribute("data-id"))
          .filter(Boolean);

        const draggedId = event.item?.getAttribute("data-id") || null;

        if (
          !draggedId ||
          orderedIds.length !== previousIds.length ||
          orderedIds.every((id, index) => id === previousIds[index])
        ) {
          restorePreviousOrder();
          return;
        }

        const previousViaPoints = manualViaPointsRef.current;

        setIsReorderingPoints(true);
        setPointsReorderError(null);
        setManualViaPoints([]);
        setViaPickMode(false);

        try {
          sortable.option("disabled", true);

          const accepted = await onReorderPointsRef.current({
            orderedIds,
            draggedId,
            oldIndex: event.oldIndex,
            newIndex: event.newIndex,
          });

          if (!accepted) {
            restorePreviousOrder();
            setManualViaPoints(previousViaPoints);
          }
        } catch (error) {
          console.error(
            "[TransportSessionMapModal] Failed to reorder points:",
            error
          );

          restorePreviousOrder();
          setManualViaPoints(previousViaPoints);
          setPointsReorderError(t("errors.reorderFailed"));
        } finally {
          try {
            sortable.option("disabled", false);
          } catch {}

          setIsReorderingPoints(false);
        }
      },
    });

    pointsSortableRef.current = sortable;

    return () => {
      if (pointsSortableRef.current === sortable) {
        pointsSortableRef.current = null;
      }

      sortable.destroy();
    };
  }, [
    isOpen,
    isPointsPanelCollapsed,
    mappedPointIdsSignature,
    mappedPoints.length,
    t,
  ]);

  useEffect(() => {
    if (!isOpen) return;

    if (routingLocations.length < 2) {
      setRouteOptions([]);
      setSelectedRouteIndex(0);
      setRouteError(null);
      setRouteLoading(false);
      return;
    }

    let cancelled = false;

    async function fetchRoadRoute() {
      setRouteLoading(true);
      setRouteError(null);

      try {
        const normalizedRoutes = await fetchPremiumRouteOptions({
          transportsessionId,
          validPoints,
          manualViaPoints,
          viaTargetIndex,
          routingLocations,
          avoidTolls,
          t,
        });

        if (cancelled) return;

        setRouteOptions(
          normalizedRoutes.length
            ? normalizedRoutes
            : [
                {
                  id: "direct-fallback",
                  label: t("routes.direct"),
                  distanceKm: null,
                  durationSeconds: null,
                  geometry: buildDirectRouteGeoJsonFromRoutingLocations(routingLocations),
                  source: "DIRECT_FALLBACK",
                },
              ]
        );
        setSelectedRouteIndex(0);
      } catch (error) {
        if (cancelled) return;

        console.warn(
          "[TransportSessionMapModal] Falling back to direct line:",
          error?.message
        );

        setRouteOptions([
          {
            id: "direct-fallback",
            label: t("routes.direct"),
            distanceKm: null,
            durationSeconds: null,
            geometry: buildDirectRouteGeoJsonFromRoutingLocations(routingLocations),
            source: "DIRECT_FALLBACK",
          },
        ]);

        setSelectedRouteIndex(0);
        setRouteError(t("errors.realRoutingUnavailable"));
      } finally {
        if (!cancelled) setRouteLoading(false);
      }
    }

    fetchRoadRoute();

    return () => {
      cancelled = true;
    };
  }, [
    isOpen,
    transportsessionId,
    routingLocations,
    validPoints,
    manualViaPoints,
    viaTargetIndex,
    avoidTolls,
    t,
  ]);

  // Cria o MapLibre apenas uma vez por abertura do modal.
  useEffect(() => {
    if (!isOpen || !mapContainerRef.current || mapRef.current) {
      return undefined;
    }

    setMapReady(false);
    setIsMapVisuallyReady(false);
    hasInitialFitRef.current = false;

    const center = validPoints[0]?.mapCoords
      ? [validPoints[0].mapCoords.lng, validPoints[0].mapCoords.lat]
      : [-8.61099, 41.14961];

    const map = new maplibregl.Map({
      container: mapContainerRef.current,
      style: MAP_STYLE,
      center,
      zoom: validPoints.length ? 8 : 5,
      attributionControl: true,
    });

    mapRef.current = map;

    let isDestroyed = false;

    map.addControl(new maplibregl.NavigationControl(), "top-right");

    const handleMapIdle = () => {
      if (isDestroyed || mapRef.current !== map) return;

      if (mapReadyFallbackTimerRef.current) {
        window.clearTimeout(mapReadyFallbackTimerRef.current);
        mapReadyFallbackTimerRef.current = null;
      }

      setIsMapVisuallyReady(true);
    };

    const handleMapLoad = () => {
      if (isDestroyed || mapRef.current !== map) return;

      setMapReady(true);

      mapResizeTimerRef.current = window.setTimeout(() => {
        if (isDestroyed || mapRef.current !== map) return;

        try {
          map.resize();
        } catch {}
      }, 80);

      map.once("idle", handleMapIdle);

      // Fallback para tiles que demorem a ficar idle.
      mapReadyFallbackTimerRef.current = window.setTimeout(() => {
        if (isDestroyed || mapRef.current !== map) return;

        setIsMapVisuallyReady(true);
      }, 900);
    };

    map.once("load", handleMapLoad);

    return () => {
      isDestroyed = true;

      // Impede imediatamente callbacks antigos de usarem este mapa.
      if (mapRef.current === map) {
        mapRef.current = null;
      }

      if (mapResizeTimerRef.current) {
        window.clearTimeout(mapResizeTimerRef.current);
        mapResizeTimerRef.current = null;
      }

      if (mapReadyFallbackTimerRef.current) {
        window.clearTimeout(mapReadyFallbackTimerRef.current);
        mapReadyFallbackTimerRef.current = null;
      }

      if (routePreviewTimeoutRef.current) {
        window.clearTimeout(routePreviewTimeoutRef.current);
        routePreviewTimeoutRef.current = null;
      }

      try {
        map.off("load", handleMapLoad);
        map.off("idle", handleMapIdle);
      } catch {}

      try {
        markersRef.current.forEach((marker) => marker.remove());
        routeLabelMarkersRef.current.forEach((marker) => marker.remove());

        routeDragMarkerRef.current?.remove();

        clearRoutePreviewLayers(map);
        clearDynamicMapLayersAndSources(map);

        map.remove();
      } catch (error) {
        console.warn(
          "[TransportSessionMapModal] Erro ignorado ao destruir mapa:",
          error?.message
        );
      }

      markersRef.current = [];
      routeLabelMarkersRef.current = [];
      routeDragMarkerRef.current = null;
      routeDragStateRef.current = null;
      routePreviewPendingPointRef.current = null;
      routePreviewRequestSeqRef.current += 1;
      hasInitialFitRef.current = false;
    };
  }, [isOpen]);

  // Atualiza rotas, pontos e labels sem destruir/recriar o mapa.
  useEffect(() => {
    if (!isOpen || !mapReady || !mapRef.current) return;

    const map = mapRef.current;
    const cleanupFns = [];

    const safeMapOff = (type, layerOrHandler, maybeHandler) => {
      try {
        if (maybeHandler) {
          map.off(type, layerOrHandler, maybeHandler);
        } else {
          map.off(type, layerOrHandler);
        }
      } catch {}
    };

    const addMapEvent = (type, handler) => {
      map.on(type, handler);
      cleanupFns.push(() => safeMapOff(type, handler));
    };

    const addLayerEvent = (type, layerId, handler) => {
      map.on(type, layerId, handler);
      cleanupFns.push(() => safeMapOff(type, layerId, handler));
    };

    const clearVisualMarkers = () => {
      markersRef.current.forEach((marker) => marker.remove());
      routeLabelMarkersRef.current.forEach((marker) => marker.remove());

      if (routeDragMarkerRef.current) {
        routeDragMarkerRef.current.remove();
      }

      markersRef.current = [];
      routeLabelMarkersRef.current = [];
      routeDragMarkerRef.current = null;
      routeDragStateRef.current = null;
    };

    const removeRouteDragMarker = () => {
      if (routeDragMarkerRef.current) {
        routeDragMarkerRef.current.remove();
        routeDragMarkerRef.current = null;
      }
    };

    const ensureRouteDragMarker = (lngLat) => {
      if (!routeDragMarkerRef.current) {
        routeDragMarkerRef.current = new maplibregl.Marker({
          element: buildRouteDragMarkerElement(),
          anchor: "center",
        })
          .setLngLat([lngLat.lng, lngLat.lat])
          .addTo(map);
      }

      routeDragMarkerRef.current.setLngLat([lngLat.lng, lngLat.lat]);
    };

    const clearRoutePreviewTimer = () => {
      if (routePreviewTimeoutRef.current) {
        window.clearTimeout(routePreviewTimeoutRef.current);
      }

      routePreviewTimeoutRef.current = null;
    };

    const requestRoutePreview = async ({ lng, lat, segmentIndex }) => {
      const requestSeq = routePreviewRequestSeqRef.current + 1;
      routePreviewRequestSeqRef.current = requestSeq;

      try {
        const previewLocations = buildRoutingLocations(
          validPoints,
          [
            ...manualViaPoints,
            {
              id: "preview-via",
              lat,
              lng,
              segmentIndex,
              order: Date.now(),
            },
          ],
          segmentIndex
        );

        if (previewLocations.length < 2) return;

        const previewRoutes = await requestRoute({
          transportsessionId,
          locations: previewLocations,
          alternatives: 0,
          avoidTolls,
          includeTollEstimate: false,
          t,
        });

        if (routePreviewRequestSeqRef.current !== requestSeq) return;

        const currentDragState = routeDragStateRef.current;

        if (!currentDragState || currentDragState.phase !== "dragging") {
          return;
        }

        const previewRoute = previewRoutes?.[0];

        if (previewRoute?.geometry) {
          setRoutePreviewGeoJson(map, previewRoute.geometry);
        }
      } catch (error) {
        console.warn(
          "[TransportSessionMapModal] Preview route unavailable:",
          error?.message
        );
      }
    };

    const scheduleRoutePreview = ({ lngLat, segmentIndex }) => {
      if (!lngLat || validPoints.length < 2) return;

      routePreviewPendingPointRef.current = {
        lng: lngLat.lng,
        lat: lngLat.lat,
        segmentIndex,
      };

      if (routePreviewTimeoutRef.current) return;

      const now = Date.now();
      const elapsed = now - routePreviewLastRequestAtRef.current;
      const waitMs = Math.max(0, 360 - elapsed);

      routePreviewTimeoutRef.current = window.setTimeout(() => {
        routePreviewTimeoutRef.current = null;

        const pendingPoint = routePreviewPendingPointRef.current;
        routePreviewPendingPointRef.current = null;

        if (!pendingPoint) return;

        routePreviewLastRequestAtRef.current = Date.now();
        requestRoutePreview(pendingPoint);
      }, waitMs);
    };

    const beginRouteDragCandidate = (event) => {
      if (!event?.lngLat || validPoints.length < 2) return;

      event.preventDefault?.();
      event.originalEvent?.preventDefault?.();
      event.originalEvent?.stopPropagation?.();

      const nearestSegmentIndex = getNearestRouteSegmentIndex({
        map,
        validPoints,
        lngLat: event.lngLat,
      });

      clearRoutePreviewTimer();
      clearRoutePreviewLayers(map);

      routePreviewPendingPointRef.current = null;
      routePreviewLastRequestAtRef.current = 0;
      routePreviewRequestSeqRef.current += 1;

      routeDragStateRef.current = {
        phase: "pending",
        segmentIndex: nearestSegmentIndex,
        startPoint: event.point,
        startLng: event.lngLat.lng,
        startLat: event.lngLat.lat,
        lng: event.lngLat.lng,
        lat: event.lngLat.lat,
      };

      map.getCanvas().style.cursor = "grab";
    };

    const handleRouteDragMove = (event) => {
      const dragState = routeDragStateRef.current;

      if (!dragState || !event?.lngLat) return;

      const movedPixels = getPixelDistance(dragState.startPoint, event.point);

      if (dragState.phase === "pending" && movedPixels < 6) {
        return;
      }

      if (dragState.phase === "pending") {
        routeDragStateRef.current = {
          ...dragState,
          phase: "dragging",
        };

        map.dragPan.disable();
        map.getCanvas().style.cursor = "grabbing";
        setRouteDraggingVisualState(map, true);
      }

      const nextDragState = {
        ...routeDragStateRef.current,
        lng: event.lngLat.lng,
        lat: event.lngLat.lat,
      };

      routeDragStateRef.current = nextDragState;

      ensureRouteDragMarker(event.lngLat);

      scheduleRoutePreview({
        lngLat: event.lngLat,
        segmentIndex: nextDragState.segmentIndex,
      });
    };

    const handleRouteDragEnd = () => {
      const dragState = routeDragStateRef.current;

      if (!dragState) return;

      clearRoutePreviewTimer();
      routePreviewPendingPointRef.current = null;
      routePreviewRequestSeqRef.current += 1;

      if (dragState.phase !== "dragging") {
        routeDragStateRef.current = null;
        clearRoutePreviewLayers(map);
        setRouteDraggingVisualState(map, false);
        map.getCanvas().style.cursor = viaPickMode ? "crosshair" : "";
        return;
      }

      const nextViaPoint = {
        id: `via-${Date.now()}`,
        lat: dragState.lat,
        lng: dragState.lng,
        segmentIndex: dragState.segmentIndex,
        order: Date.now(),
      };

      setViaTargetIndex(dragState.segmentIndex);
      setManualViaPoints((prev) => [...prev, nextViaPoint]);
      setViaPickMode(false);

      routeDragStateRef.current = null;
      removeRouteDragMarker();

      map.dragPan.enable();
      setRouteDraggingVisualState(map, false);
      map.getCanvas().style.cursor = "";
    };

    clearVisualMarkers();
    clearDynamicMapLayersAndSources(map);

    if (viaPickMode) {
      map.getCanvas().style.cursor = "crosshair";
    } else {
      map.getCanvas().style.cursor = "";
    }

    addMapEvent("mousemove", handleRouteDragMove);
    addMapEvent("mouseup", handleRouteDragEnd);

    const fallbackRoute =
      routingLocations.length >= 2
        ? {
            id: "direct-fallback",
            label: t("routes.direct"),
            distanceKm: null,
            durationSeconds: null,
            geometry: buildDirectRouteGeoJsonFromRoutingLocations(routingLocations),
            source: "DIRECT_FALLBACK",
          }
        : null;

    const drawableRoutes =
      routeOptions.length > 0
        ? routeOptions.filter((route) => route?.geometry)
        : fallbackRoute
          ? [fallbackRoute]
          : [];

    const safeSelectedRouteIndex = Math.min(
      Math.max(selectedRouteIndex, 0),
      Math.max(drawableRoutes.length - 1, 0)
    );

    const selectedDrawableRoute =
      drawableRoutes[safeSelectedRouteIndex] || drawableRoutes[0] || null;

    const selectedRouteGeoJson = selectedDrawableRoute?.geometry || null;

    drawableRoutes.forEach((route, index) => {
      if (!route?.geometry || index === safeSelectedRouteIndex) return;

      const sourceId = `transport-session-route-alt-${index}`;

      map.addSource(sourceId, {
        type: "geojson",
        data: route.geometry,
      });

      map.addLayer({
        id: `${sourceId}-casing`,
        type: "line",
        source: sourceId,
        layout: {
          "line-cap": "round",
          "line-join": "round",
        },
        paint: {
          "line-color": "#ffffff",
          "line-width": 8,
          "line-opacity": 0.8,
        },
      });

      map.addLayer({
        id: `${sourceId}-line`,
        type: "line",
        source: sourceId,
        layout: {
          "line-cap": "round",
          "line-join": "round",
        },
        paint: {
          "line-color": "#6d5dfc",
          "line-width": 4,
          "line-opacity": 0.52,
        },
      });

      map.addLayer({
        id: `${sourceId}-hitbox`,
        type: "line",
        source: sourceId,
        layout: {
          "line-cap": "round",
          "line-join": "round",
        },
        paint: {
          "line-color": "#000000",
          "line-width": 24,
          "line-opacity": 0,
        },
      });

      addLayerEvent("mousedown", `${sourceId}-hitbox`, beginRouteDragCandidate);

      addLayerEvent("click", `${sourceId}-hitbox`, (event) => {
        event.originalEvent?.stopPropagation?.();
        setSelectedRouteIndex(index);
      });

      addLayerEvent("mouseenter", `${sourceId}-hitbox`, () => {
        map.getCanvas().style.cursor = "grab";

        if (map.getLayer(`${sourceId}-line`)) {
          map.setPaintProperty(`${sourceId}-line`, "line-width", 5);
          map.setPaintProperty(`${sourceId}-line`, "line-opacity", 0.72);
        }
      });

      addLayerEvent("mouseleave", `${sourceId}-hitbox`, () => {
        if (!routeDragStateRef.current) {
          map.getCanvas().style.cursor = viaPickMode ? "crosshair" : "";
        }

        if (map.getLayer(`${sourceId}-line`)) {
          map.setPaintProperty(`${sourceId}-line`, "line-width", 4);
          map.setPaintProperty(`${sourceId}-line`, "line-opacity", 0.52);
        }
      });
    });

    if (selectedRouteGeoJson && routingLocations.length >= 2) {
      map.addSource("transport-session-route", {
        type: "geojson",
        data: selectedRouteGeoJson,
      });

      map.addLayer({
        id: "transport-session-route-casing",
        type: "line",
        source: "transport-session-route",
        layout: {
          "line-cap": "round",
          "line-join": "round",
        },
        paint: {
          "line-color": "#ffffff",
          "line-width": 9,
          "line-opacity": 0.95,
        },
      });

      map.addLayer({
        id: "transport-session-route-line",
        type: "line",
        source: "transport-session-route",
        layout: {
          "line-cap": "round",
          "line-join": "round",
        },
        paint: {
          "line-color":
            selectedDrawableRoute?.source === "DIRECT_FALLBACK"
              ? "#111827"
              : "#3214d6",
          "line-width": 5,
          "line-opacity": 0.98,
          "line-dasharray":
            selectedDrawableRoute?.source === "DIRECT_FALLBACK"
              ? [1.6, 1.1]
              : [1, 0],
        },
      });

      map.addLayer({
        id: "transport-session-route-hitbox",
        type: "line",
        source: "transport-session-route",
        layout: {
          "line-cap": "round",
          "line-join": "round",
        },
        paint: {
          "line-color": "#000000",
          "line-width": 26,
          "line-opacity": 0,
        },
      });

      addLayerEvent(
        "mousedown",
        "transport-session-route-hitbox",
        beginRouteDragCandidate
      );

      addLayerEvent("mouseenter", "transport-session-route-hitbox", () => {
        map.getCanvas().style.cursor = "grab";

        if (map.getLayer("transport-session-route-line")) {
          map.setPaintProperty("transport-session-route-line", "line-width", 6);
        }
      });

      addLayerEvent("mouseleave", "transport-session-route-hitbox", () => {
        if (!routeDragStateRef.current) {
          map.getCanvas().style.cursor = viaPickMode ? "crosshair" : "";
        }

        if (map.getLayer("transport-session-route-line")) {
          map.setPaintProperty("transport-session-route-line", "line-width", 5);
        }
      });

      addLayerEvent("click", "transport-session-route-hitbox", (event) => {
        event.originalEvent?.stopPropagation?.();
        setSelectedRouteIndex(safeSelectedRouteIndex);
      });
    }

    const blockedLabelCoordinates = [
      ...validPoints.map((point) => [
        point.mapCoords.lng,
        point.mapCoords.lat,
      ]),
      ...manualViaPoints.map((viaPoint) => [viaPoint.lng, viaPoint.lat]),
    ];

    const usedRouteLabelPixelPositions = [];

    drawableRoutes.forEach((route, index) => {
      const isActiveRoute = index === safeSelectedRouteIndex;

      const labelCoordinate = pickRouteLabelCoordinate({
        route,
        map,
        blockedCoordinates: blockedLabelCoordinates,
        usedLabelPixelPositions: usedRouteLabelPixelPositions,
      });

      const duration = formatDuration(route?.durationSeconds, t);
      const distance = formatKm(route?.distanceKm, locale);

      if (!labelCoordinate || (!duration && !distance)) return;

      const labelEl = document.createElement("button");
      labelEl.type = "button";
      labelEl.className = [
        "ts-map-route-label-marker",
        isActiveRoute ? "is-active" : "is-muted",
      ]
        .filter(Boolean)
        .join(" ");

      labelEl.innerHTML = `
        <span class="ts-map-route-label-icon ts-map-route-label-icon--truck" aria-hidden="true"></span>
        <span class="ts-map-route-label-main">
          ${duration ? `<strong>${escapeHtml(duration)}</strong>` : ""}
          ${distance ? `<small>${escapeHtml(distance)}</small>` : ""}
        </span>
      `;

      labelEl.addEventListener("click", (event) => {
        event.stopPropagation();
        setSelectedRouteIndex(index);
      });

      const labelMarker = new maplibregl.Marker({
        element: labelEl,
        anchor: "center",
      })
        .setLngLat(labelCoordinate)
        .addTo(map);

      routeLabelMarkersRef.current.push(labelMarker);
      usedRouteLabelPixelPositions.push(map.project(labelCoordinate));
    });

    const pointFeatureCollection = buildMarkerFeatureCollection(markerGroups, t);

    if (pointFeatureCollection.features.length) {
      map.addSource("transport-session-points", {
        type: "geojson",
        data: pointFeatureCollection,
      });

      map.addLayer({
        id: "transport-session-points-halo",
        type: "circle",
        source: "transport-session-points",
        paint: {
          "circle-radius": [
            "case",
            [">", ["get", "groupSize"], 1],
            19,
            17,
          ],
          "circle-color": "#ffffff",
          "circle-opacity": 0.96,
          "circle-stroke-color": "rgba(15, 23, 42, 0.16)",
          "circle-stroke-width": 1,
        },
      });

      map.addLayer({
        id: "transport-session-points-circle",
        type: "circle",
        source: "transport-session-points",
        paint: {
          "circle-radius": [
            "case",
            [">", ["get", "groupSize"], 1],
            15,
            13,
          ],
          "circle-color": [
            "match",
            ["get", "markerClass"],
            "completed",
            "#16a34a",
            "cancelled",
            "#dc2626",
            "active",
            "#2563eb",
            "delayed",
            "#f59e0b",
            "pending",
            "#111827",
            "#64748b",
          ],
          "circle-stroke-color": "#ffffff",
          "circle-stroke-width": 2,
          "circle-opacity": 0.98,
        },
      });

      map.addLayer({
        id: "transport-session-points-label",
        type: "symbol",
        source: "transport-session-points",
        layout: {
          "text-field": ["get", "label"],
          "text-size": 11,
          "text-allow-overlap": true,
          "text-ignore-placement": true,
        },
        paint: {
          "text-color": "#ffffff",
          "text-halo-color": "rgba(15, 23, 42, 0.08)",
          "text-halo-width": 0.4,
        },
      });

      const showPointPopup = (event) => {
        event.originalEvent?.stopPropagation?.();

        const feature = event.features?.[0];
        const coordinates = feature?.geometry?.coordinates?.slice();
        const popupHtml = feature?.properties?.popupHtml;

        if (!coordinates || !popupHtml) return;

        new maplibregl.Popup({
          offset: 18,
          closeButton: false,
        })
          .setLngLat(coordinates)
          .setHTML(popupHtml)
          .addTo(map);
      };

      addLayerEvent("click", "transport-session-points-circle", showPointPopup);
      addLayerEvent("click", "transport-session-points-label", showPointPopup);

      addLayerEvent("mouseenter", "transport-session-points-circle", () => {
        map.getCanvas().style.cursor = "pointer";
      });

      addLayerEvent("mouseenter", "transport-session-points-label", () => {
        map.getCanvas().style.cursor = "pointer";
      });

      addLayerEvent("mouseleave", "transport-session-points-circle", () => {
        map.getCanvas().style.cursor = viaPickMode ? "crosshair" : "";
      });

      addLayerEvent("mouseleave", "transport-session-points-label", () => {
        map.getCanvas().style.cursor = viaPickMode ? "crosshair" : "";
      });
    }

  manualViaPoints.forEach((viaPoint) => {
    const markerEl = document.createElement("div");
    markerEl.className = "ts-map-via-marker";
    markerEl.innerHTML = `<span aria-hidden="true"></span>`;

    markerEl.addEventListener("dblclick", (event) => {
      event.stopPropagation();

      setManualViaPoints((prev) =>
        prev.filter((item) => String(item.id) !== String(viaPoint.id))
      );
    });

    const snappedViaCoordinate = getNearestCoordinateOnRoute({
      map,
      route: selectedDrawableRoute,
      lng: viaPoint.lng,
      lat: viaPoint.lat,
    });

    const marker = new maplibregl.Marker({
      element: markerEl,
      anchor: "center",
      draggable: true,
    })
      .setLngLat(snappedViaCoordinate)
      .setPopup(
        new maplibregl.Popup({
          offset: 14,
          closeButton: false,
        }).setHTML(`
          <div class="ts-map-popup">
            <strong>${escapeHtml(t("routes.viaPopup.title"))}</strong>
            <span>${escapeHtml(t("routes.viaPopup.description"))}</span>
          </div>
        `)
      )
      .addTo(map);

      marker.on("dragstart", () => {
        map.dragPan.disable();
        map.getCanvas().style.cursor = "grabbing";
      });

      marker.on("dragend", () => {
        const lngLat = marker.getLngLat();

        const segmentIndex = getNearestRouteSegmentIndex({
          map,
          validPoints,
          lngLat,
        });

        setViaTargetIndex(segmentIndex);

        setManualViaPoints((prev) =>
          prev.map((item) =>
            String(item.id) === String(viaPoint.id)
              ? {
                  ...item,
                  lat: lngLat.lat,
                  lng: lngLat.lng,
                  segmentIndex,
                }
              : item
          )
        );

        setViaPickMode(false);

        window.setTimeout(() => {
          map.dragPan.enable();
          map.getCanvas().style.cursor = "";
        }, 0);
      });

      markersRef.current.push(marker);
    });

    const handleMapClick = (event) => {
      if (!viaPickMode) return;

      const { lng, lat } = event.lngLat;

      const segmentIndex = getNearestRouteSegmentIndex({
        map,
        validPoints,
        lngLat: event.lngLat,
      });

      setViaTargetIndex(segmentIndex);

      setManualViaPoints((prev) => [
        ...prev,
        {
          id: `via-${Date.now()}`,
          lat,
          lng,
          segmentIndex,
          order: Date.now(),
        },
      ]);

      setViaPickMode(false);
    };

    addMapEvent("click", handleMapClick);

    const bounds = new maplibregl.LngLatBounds();

    validPoints.forEach((point) => {
      bounds.extend([point.mapCoords.lng, point.mapCoords.lat]);
    });

    manualViaPoints.forEach((viaPoint) => {
      bounds.extend([viaPoint.lng, viaPoint.lat]);
    });

    if (
      selectedRouteGeoJson?.geometry?.type === "LineString" &&
      Array.isArray(selectedRouteGeoJson.geometry.coordinates)
    ) {
      selectedRouteGeoJson.geometry.coordinates.forEach((coord) => {
        if (Array.isArray(coord) && coord.length >= 2) {
          bounds.extend(coord);
        }
      });
    }

    if (!hasInitialFitRef.current && validPoints.length >= 2) {
      map.fitBounds(bounds, {
        padding: {
          top: 126,
          bottom: 86,
          left: 92,
          right: 92,
        },
        maxZoom: 13,
        duration: 650,
      });

      hasInitialFitRef.current = true;
    } else if (!hasInitialFitRef.current && validPoints.length === 1) {
      map.flyTo({
        center: [validPoints[0].mapCoords.lng, validPoints[0].mapCoords.lat],
        zoom: 12,
        duration: 600,
      });

      hasInitialFitRef.current = true;
    }

    return () => {
      cleanupFns.forEach((cleanup) => cleanup());

      try {
        map.dragPan.enable();
      } catch {}

      clearRoutePreviewTimer();
      routePreviewPendingPointRef.current = null;
      routePreviewRequestSeqRef.current += 1;
      if (mapRef.current === map) {
        try {
          setRouteDraggingVisualState(map, false);
        } catch {}
      }

      // Não limpar layers/markers aqui.
      // O cleanup corre antes de cada redraw e isso criava o "piscar".
      // As layers/markers são limpas apenas no início do próximo redraw,
      // imediatamente antes de serem adicionadas outra vez.
    };
  }, [
    isOpen,
    mapReady,
    validPoints,
    markerGroups,
    manualViaPoints,
    viaPickMode,
    routeOptions,
    selectedRouteIndex,
    routingLocations,
    avoidTolls,
    locale,
    t,
  ]);

  useEffect(() => {
    if (!isOpen || !mapRef.current) return;

    const timer = window.setTimeout(() => {
      mapRef.current?.resize();
    }, 120);

    return () => window.clearTimeout(timer);
  }, [isOpen, isPointsPanelCollapsed]);


  const handleApplySavedRoute =
  async (routeTemplate) => {
    if (
      !hasRouteTemplateAccess
    ) {
      setSavedRoutesError(
        t(
          "savedRoutes.errors.forbidden",
          {
            defaultValue:
              "Não tem permissão para aplicar rotas guardadas.",
          }
        )
      );

      return;
    }

    if (!routeTemplate?.id) {
      return;
    }

    setApplyingRouteTemplateId(
      routeTemplate.id
    );

    setSavedRoutesError(null);

    try {
      const { data } =
        await axios.put(
          `/transportsessions/${transportsessionId}/route-template`,
          {
            routeTemplateId:
              routeTemplate.id,
          }
        );

      const snapshot =
        data?.routeSnapshot ||
        data?.transportSession
          ?.routeSnapshot ||
        null;

      if (!snapshot) {
        throw new Error(
          "Route snapshot missing from response"
        );
      }

      setAppliedRouteSnapshot(
        snapshot
      );

      setAvoidTolls(
        Boolean(
          snapshot.avoidTolls
        )
      );

      const restoredViaPoints =
        buildSnapshotViaPoints({
          snapshot,
          validPoints,
          map:
            mapRef.current,
        });

      setManualViaPoints(
        restoredViaPoints
      );

      setViaTargetIndex(
        restoredViaPoints[0]
          ?.segmentIndex || 0
      );

      routeSnapshotRestoreSignatureRef.current =
        [
          snapshot.templateId,
          snapshot.templateVersion,
          snapshot.appliedAt,
        ].join(":");

      setViaPickMode(false);
      setIsSavedRoutesOpen(false);
      setSavedRoutesSearch("");
    } catch (error) {
      console.error(
        "[TransportSessionMapModal] Failed to apply route template:",
        error
      );

      setSavedRoutesError(
        error?.response?.data
          ?.error ||
        t(
          "savedRoutes.errors.apply"
        )
      );
    } finally {
      setApplyingRouteTemplateId(
        null
      );
    }
  };

const handleRemoveSavedRoute =
  async () => {
    if (
      !hasRouteTemplateAccess
    ) {
      setSavedRoutesError(
        t(
          "savedRoutes.errors.forbidden",
          {
            defaultValue:
              "Não tem permissão para remover a rota guardada.",
          }
        )
      );

      return;
    }

    setRemovingRouteTemplate(true);
    setSavedRoutesError(null);

    try {
      await axios.delete(
        `/transportsessions/${transportsessionId}/route-template`
      );

      setAppliedRouteSnapshot(null);
      setManualViaPoints([]);
      setViaTargetIndex(0);
      setViaPickMode(false);
      setAvoidTolls(false);

      routeSnapshotRestoreSignatureRef.current =
        "";
    } catch (error) {
      console.error(
        "[TransportSessionMapModal] Failed to remove route template:",
        error
      );

      setSavedRoutesError(
        error?.response?.data
          ?.error ||
        t(
          "savedRoutes.errors.remove"
        )
      );
    } finally {
      setRemovingRouteTemplate(false);
    }
  };

  const handleSaveSessionRoute =
    async () => {
      if (
        !hasRouteTemplateAccess
      ) {
        setSessionRouteSaveError(
          t(
            "sharedRoute.errors.forbidden",
            {
              defaultValue:
                "Não tem permissão para partilhar esta rota.",
            }
          )
        );

        return;
      }

      if (
        !transportsessionId
      ) {
        setSessionRouteSaveError(
          t(
            "sharedRoute.errors.session",
            {
              defaultValue:
                "A Transport Session não foi identificada.",
            }
          )
        );

        return;
      }

      if (
        validPoints.length < 2
      ) {
        setSessionRouteSaveError(
          t(
            "sharedRoute.errors.points",
            {
              defaultValue:
                "São necessários pelo menos dois pontos com coordenadas.",
            }
          )
        );

        return;
      }

      if (
        !selectedRoute?.geometry
      ) {
        setSessionRouteSaveError(
          t(
            "sharedRoute.errors.geometry",
            {
              defaultValue:
                "A rota ainda não possui uma geometria válida.",
            }
          )
        );

        return;
      }

      if (
        String(
          selectedRoute?.source ||
          ""
        ).toUpperCase() ===
        "DIRECT_FALLBACK"
      ) {
        setSessionRouteSaveError(
          t(
            "sharedRoute.errors.direct",
            {
              defaultValue:
                "Não é possível partilhar uma linha direta. Aguarde pelo cálculo da rota através das estradas.",
            }
          )
        );

        return;
      }

      const lastRoutingIndex =
        routingLocations.length - 1;

      const routeControlLocations =
        routingLocations.filter(
          (
            location,
            index
          ) => {
            const isEndpoint =
              index === 0 ||
              index ===
                lastRoutingIndex;

            return (
              isEndpoint ||
              location?.source ===
                "MANUAL_VIA_POINT"
            );
          }
        );

      const pointsPayload =
        routeControlLocations.map(
          (
            location,
            index
          ) => {
            const isFirst =
              index === 0;

            const isLast =
              index ===
              routeControlLocations.length -
                1;

            const manualVia =
              manualViaPoints.find(
                (point) =>
                  String(
                    point?.id
                  ) ===
                  String(
                    location?.id
                  )
              ) ||
              null;

            const rawPickupDeliveryId =
              location?.source ===
              "SESSION_POINT"
                ? Number(
                    location?.id
                  )
                : null;

            const pickupDeliveryId =
              Number.isInteger(
                rawPickupDeliveryId
              ) &&
              rawPickupDeliveryId > 0
                ? rawPickupDeliveryId
                : null;

            return {
              id:
                location?.id != null
                  ? String(
                      location.id
                    )
                  : null,

              pickupDeliveryId,

              type:
                isFirst
                  ? "ORIGIN"
                  : isLast
                    ? "DESTINATION"
                    : "VIA",

              operationalType:
                location?.type ||
                null,

              sequence:
                index + 1,

              lat:
                Number(
                  location.lat
                ),

              lng:
                Number(
                  location.lon
                ),

              source:
                location?.source ||
                (
                  pickupDeliveryId
                    ? "SESSION_POINT"
                    : "MANUAL_VIA_POINT"
                ),

              segmentIndex:
                manualVia
                  ?.segmentIndex ??
                null,

              order:
                manualVia
                  ?.order ??
                index + 1,

              name:
                manualVia
                  ?.name ||
                null,

              address:
                manualVia
                  ?.address ||
                null,
            };
          }
        );

      setSavingSessionRoute(true);
      setSessionRouteSaveError(null);

      try {
        const { data } =
          await axios.put(
            `/transportsessions/${transportsessionId}/route`,
            {
              name:
                appliedRouteSnapshot
                  ?.name ||
                `Rota da sessão ${transportsessionId}`,

              description:
                appliedRouteSnapshot
                  ?.description ||
                null,

              routeNumber:
                appliedRouteSnapshot
                  ?.routeNumber ||
                null,

              vehicleProfile:
                appliedRouteSnapshot
                  ?.vehicleProfile ||
                "truck",

              settings: {
                avoidTolls:
                  Boolean(
                    avoidTolls
                  ),

                avoidFerries:
                  Boolean(
                    appliedRouteSnapshot
                      ?.avoidFerries
                  ),

                avoidHighways:
                  Boolean(
                    appliedRouteSnapshot
                      ?.avoidHighways
                  ),
              },

              points:
                pointsPayload,

              route: {
                distanceKm:
                  selectedRoute
                    ?.distanceKm ??
                  null,

                durationSeconds:
                  selectedRoute
                    ?.durationSeconds ??
                  null,

                durationNoTrafficSeconds:
                  selectedRoute
                    ?.durationNoTrafficSeconds ??
                  null,

                routeName:
                  selectedRoute
                    ?.routeName ??
                  null,

                roadNames:
                  Array.isArray(
                    selectedRoute
                      ?.roadNames
                  )
                    ? selectedRoute
                        .roadNames
                    : [],

                hasTolls:
                  routeHasTolls(
                    selectedRoute
                  ),

                tollText:
                  selectedRoute
                    ?.tollText ??
                  null,

                tollDistanceKm:
                  selectedRoute
                    ?.tollDistanceKm ??
                  null,

                tollRoadNames:
                  Array.isArray(
                    selectedRoute
                      ?.tollRoadNames
                  )
                    ? selectedRoute
                        .tollRoadNames
                    : [],

                tollAnalysisStatus:
                  selectedRoute
                    ?.tollAnalysisStatus ??
                  null,

                tollEstimate:
                  selectedRoute
                    ?.tollEstimate ??
                  null,

                hasHighways:
                  routeUsesHighways(
                    selectedRoute
                  ),

                geometry:
                  selectedRoute
                    .geometry,

                source:
                  selectedRoute
                    ?.source ||
                  "VALHALLA",
              },
            }
          );

        const snapshot =
          data?.routeSnapshot ||
          null;

        if (!snapshot) {
          throw new Error(
            "Route snapshot missing from response"
          );
        }

        setAppliedRouteSnapshot(
          snapshot
        );

        routeSnapshotRestoreSignatureRef.current =
          [
            snapshot.templateId,
            snapshot.templateVersion,
            snapshot.appliedAt,
          ].join(":");

        setSessionRouteSaveError(null);
        setViaPickMode(false);
      } catch (error) {
        console.error(
          "[TransportSessionMapModal] Failed to share session route:",
          error
        );

        setSessionRouteSaveError(
          error?.response?.data
            ?.error ||
          t(
            "sharedRoute.errors.save",
            {
              defaultValue:
                "Não foi possível partilhar a rota com o motorista.",
            }
          )
        );
      } finally {
        setSavingSessionRoute(false);
      }
    };

  automaticRouteSaveActionRef.current =
    handleSaveSessionRoute;

  useEffect(() => {
    if (
      automaticRouteSaveTimerRef.current
    ) {
      window.clearTimeout(
        automaticRouteSaveTimerRef.current
      );

      automaticRouteSaveTimerRef.current =
        null;
    }

    if (
      !isOpen ||
      !hasRouteTemplateAccess ||
      !transportsessionId ||
      routeLoading ||
      savingSessionRoute ||
      validPoints.length < 2 ||
      !selectedRoute?.geometry ||
      String(
        selectedRoute?.source ||
        ""
      ).toUpperCase() ===
        "DIRECT_FALLBACK"
    ) {
      return undefined;
    }

    const routeCoordinates =
      getLineStringCoordinates(
        selectedRoute
      );

    if (
      routeCoordinates.length < 2
    ) {
      return undefined;
    }

    const automaticSaveSignature =
      JSON.stringify({
        transportsessionId:
          Number(
            transportsessionId
          ),

        mappedPointIdsSignature,

        selectedRouteIndex,

        routeId:
          selectedRoute?.id ||
          null,

        distanceKm:
          selectedRoute
            ?.distanceKm ??
          null,

        durationSeconds:
          selectedRoute
            ?.durationSeconds ??
          null,

        coordinateCount:
          routeCoordinates.length,

        firstCoordinate:
          routeCoordinates[0] ||
          null,

        lastCoordinate:
          routeCoordinates[
            routeCoordinates.length -
            1
          ] ||
          null,

        avoidTolls:
          Boolean(
            avoidTolls
          ),

        manualViaPoints:
          manualViaPoints.map(
            (point) => ({
              id:
                point?.id ||
                null,

              lat:
                Number(
                  point?.lat
                ),

              lng:
                Number(
                  point?.lng
                ),

              segmentIndex:
                point
                  ?.segmentIndex ??
                null,

              order:
                point?.order ??
                null,
            })
          ),
      });

    if (
      automaticRouteSaveSignatureRef.current ===
      automaticSaveSignature
    ) {
      return undefined;
    }

    automaticRouteSaveTimerRef.current =
      window.setTimeout(
        async () => {
          automaticRouteSaveTimerRef.current =
            null;

          automaticRouteSaveSignatureRef.current =
            automaticSaveSignature;

          await automaticRouteSaveActionRef
            .current?.();
        },
        900
      );

    return () => {
      if (
        automaticRouteSaveTimerRef.current
      ) {
        window.clearTimeout(
          automaticRouteSaveTimerRef.current
        );

        automaticRouteSaveTimerRef.current =
          null;
      }
    };
  }, [
    avoidTolls,
    hasRouteTemplateAccess,
    isOpen,
    manualViaPoints,
    mappedPointIdsSignature,
    routeLoading,
    savingSessionRoute,
    selectedRoute,
    selectedRouteIndex,
    transportsessionId,
    validPoints.length,
  ]);

  if (!isOpen) return null;

  const selectedDistance = formatKm(selectedRoute?.distanceKm, locale);
  const selectedDuration = formatDuration(selectedRoute?.durationSeconds, t);

  return (
    <div
      className="ts-map-modal-backdrop"
      onMouseDown={handleBackdropMouseDown}
    >
      <div
        className="ts-map-modal"
        role="dialog"
        aria-modal="true"
        aria-label={t("header.dialogAria")}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="ts-map-modal-header">
          <div>
            <p className="ts-map-modal-eyebrow">{t("header.eyebrow")}</p>
            <h2>{t("header.title", { id: transportsessionId })}</h2>
          </div>

          <button
            type="button"
            className="ts-map-modal-close"
            onClick={handleClose}
            aria-label={t("header.closeAria")}
          >
            ×
          </button>
        </div>

        <div
          className={`ts-map-modal-body ${
            isPointsPanelCollapsed ? "is-points-collapsed" : ""
          }`}
        >
          <div
            className={`ts-map-canvas-wrap ${
              isMapVisuallyReady ? "is-map-ready" : "is-map-loading"
            }`}
          >
            <div ref={mapContainerRef} className="ts-map-canvas" />

            {!isMapVisuallyReady && (
              <div className="ts-map-loading-cover">
                <div className="ts-map-loading-card">
                  <span className="ts-map-loading-spinner" />
                  <strong>{t("loading.map")}</strong>
                </div>
              </div>
            )}

            <div className="ts-map-route-toolbar">
              <div className="ts-map-route-status">
                {routeLoading ? (
                  <span>{t("loading.route")}</span>
                ) : manualViaPoints.length > 0 ? (
                  <span>{t("routes.status.withVia")}</span>
                ) : selectedRoute?.source === "DIRECT_FALLBACK" ? (
                  <span>{t("routes.direct")}</span>
                ) : (
                  <span>{t("routes.status.byRoad")}</span>
                )}

                {(selectedDistance || selectedDuration) && (
                  <strong>
                    {[selectedDuration, selectedDistance]
                      .filter(Boolean)
                      .join(" · ")}
                  </strong>
                )}
              </div>

              {appliedRouteSnapshot && (
                <div className="ts-map-saved-route-applied">
                  <div>
                    <span>
                      {t(
                        "savedRoutes.applied"
                      )}
                    </span>

                    <strong>
                      {appliedRouteSnapshot
                        .name}
                    </strong>

                    <small>
                      {appliedRouteSnapshot
                        .templateId
                        ? t(
                            "savedRoutes.appliedMeta",
                            {
                              number:
                                appliedRouteSnapshot
                                  .routeNumber ||
                                appliedRouteSnapshot
                                  .templateId,

                              version:
                                appliedRouteSnapshot
                                  .templateVersion ||
                                1,
                            }
                          )
                        : t(
                            "sharedRoute.meta",
                            {
                              revision:
                                appliedRouteSnapshot
                                  .revision ||
                                1,

                              defaultValue:
                                "Partilhada com o motorista · revisão {{revision}}",
                            }
                          )}
                    </small>
                  </div>

                  {hasRouteTemplateAccess && (
                    <button
                      type="button"
                      onClick={
                        handleRemoveSavedRoute
                      }
                      disabled={
                        removingRouteTemplate
                      }
                    >
                      {removingRouteTemplate
                        ? t(
                            "savedRoutes.removing"
                          )
                        : t(
                            "savedRoutes.remove"
                          )}
                    </button>
                  )}
                </div>
              )}

              <div className="ts-map-route-tools">
                {hasRouteTemplateAccess && (
                  <button
                    type="button"
                    className={`ts-map-route-tool-btn is-saved-route ${
                      isSavedRoutesOpen
                        ? "is-active"
                        : ""
                    }`}
                    onClick={() =>
                      setIsSavedRoutesOpen(
                        (current) =>
                          !current
                      )
                    }
                  >
                    {appliedRouteSnapshot
                      ? t(
                          "savedRoutes.change"
                        )
                      : t(
                          "savedRoutes.use"
                        )}
                  </button>
                )}

                <button
                  type="button"
                  className={`ts-map-route-tool-btn ${
                    viaPickMode ? "is-active" : ""
                  }`}
                  onClick={() =>
                    setViaPickMode(
                      (current) =>
                        !current
                    )
                  }
                  disabled={
                    validPoints.length < 2
                  }
                >
                  {viaPickMode
                    ? t(
                        "routes.actions.pickViaActive"
                      )
                    : t(
                        "routes.actions.pickRoad"
                      )}
                </button>

                <button
                  type="button"
                  className={`ts-map-route-tool-btn is-toll-filter ${
                    avoidTolls
                      ? "is-active"
                      : ""
                  }`}
                  onClick={() =>
                    setAvoidTolls(
                      (current) =>
                        !current
                    )
                  }
                  disabled={
                    validPoints.length < 2 ||
                    routeLoading
                  }
                  aria-pressed={
                    avoidTolls
                  }
                  title={t(
                    "routes.actions.avoidTollsTitle"
                  )}
                >
                  {avoidTolls
                    ? t(
                        "routes.actions.avoidTollsActive"
                      )
                    : t(
                        "routes.actions.avoidTolls"
                      )}
                </button>

                {hasManualRouteAdjustments && (
                  <button
                    type="button"
                    className="ts-map-route-tool-btn is-clear"
                    onClick={() => {
                      setManualViaPoints(
                        (current) =>
                          current.filter(
                            (point) =>
                              point?.source ===
                              "ROUTE_TEMPLATE"
                          )
                      );

                      setViaPickMode(false);
                    }}
                  >
                    {t(
                      "routes.actions.clearVia"
                    )}
                  </button>
                )}
              </div>

              {sessionRouteSaveError && (
                <div className="ts-map-route-error">
                  {sessionRouteSaveError}
                </div>
              )}

              {hasRouteTemplateAccess &&
                isSavedRoutesOpen && (
                <div className="ts-map-saved-routes">
                  <div className="ts-map-saved-routes-header">
                    <div>
                      <strong>
                        {t(
                          "savedRoutes.title"
                        )}
                      </strong>

                      <span>
                        {t(
                          "savedRoutes.description"
                        )}
                      </span>
                    </div>

                    <button
                      type="button"
                      onClick={() =>
                        setIsSavedRoutesOpen(
                          false
                        )
                      }
                      aria-label={t(
                        "savedRoutes.closeAria"
                      )}
                    >
                      ×
                    </button>
                  </div>

                  <input
                    type="search"
                    value={
                      savedRoutesSearch
                    }
                    onChange={(event) =>
                      setSavedRoutesSearch(
                        event.target.value
                      )
                    }
                    placeholder={t(
                      "savedRoutes.searchPlaceholder"
                    )}
                  />

                  {savedRoutesError && (
                    <div className="ts-map-saved-routes-error">
                      {savedRoutesError}
                    </div>
                  )}

                  {savedRoutesLoading ? (
                    <div className="ts-map-saved-routes-loading">
                      {t(
                        "savedRoutes.loading"
                      )}
                    </div>
                  ) : filteredSavedRoutes
                      .length > 0 ? (
                    <div className="ts-map-saved-routes-list">
                      {filteredSavedRoutes.map(
                        (routeTemplate) => {
                          const duration =
                            formatDuration(
                              routeTemplate
                                ?.durationSeconds,
                              t
                            );

                          const distance =
                            formatKm(
                              routeTemplate
                                ?.distanceKm,
                              locale
                            );

                          const isApplying =
                            Number(
                              applyingRouteTemplateId
                            ) ===
                            Number(
                              routeTemplate.id
                            );

                          return (
                            <div
                              key={
                                routeTemplate.id
                              }
                              className="ts-map-saved-route-card"
                            >
                              <div>
                                <span>
                                  {`R-${String(
                                    routeTemplate
                                      .routeNumber ||
                                      routeTemplate
                                        .id
                                  ).padStart(
                                    4,
                                    "0"
                                  )}`}
                                </span>

                                <strong>
                                  {
                                    routeTemplate.name
                                  }
                                </strong>

                                <small>
                                  {[
                                    duration,
                                    distance,
                                    t(
                                      "savedRoutes.pointCount",
                                      {
                                        count:
                                          routeTemplate
                                            ?.points
                                            ?.length ||
                                          0,
                                      }
                                    ),
                                  ]
                                    .filter(
                                      Boolean
                                    )
                                    .join(
                                      " · "
                                    )}
                                </small>
                              </div>

                              <button
                                type="button"
                                disabled={
                                  isApplying ||
                                  applyingRouteTemplateId !==
                                    null
                                }
                                onClick={() =>
                                  handleApplySavedRoute(
                                    routeTemplate
                                  )
                                }
                              >
                                {isApplying
                                  ? t(
                                      "savedRoutes.applying"
                                    )
                                  : t(
                                      "savedRoutes.apply"
                                    )}
                              </button>
                            </div>
                          );
                        }
                      )}
                    </div>
                  ) : savedRoutes.length >
                    0 ? (
                    <div className="ts-map-saved-routes-empty">
                      {t(
                        "savedRoutes.noResults"
                      )}
                    </div>
                  ) : (
                    <div className="ts-map-saved-routes-empty">
                      {t(
                        "savedRoutes.noRoutes"
                      )}
                    </div>
                  )}
                </div>
              )}

              {viaPickMode && (
                <div className="ts-map-route-hint">
                  {t("routes.actions.pickViaHint")}
                </div>
              )}

              {routeOptions.length > 0 && (
                <div
                  className="ts-map-route-options"
                  aria-label={t("routes.optionsAria")}
                >
                  {routeOptions.map((route, index) => {
                    const duration = formatDuration(route?.durationSeconds, t);
                    const distance = formatKm(route?.distanceKm, locale);
                    const noTrafficLabel = getRouteNoTrafficLabel(route, t);
                    const hasTolls = routeHasTolls(route);
                    const hasHighways = routeUsesHighways(route);
                    const roadLabels = getRouteRoadLabels(route);
                    const warningLabel = getRouteWarningLabel(
                      route,
                      avoidTolls,
                      t
                    );
                    const hasReliableRouteData =
                      route?.source !== "DIRECT_FALLBACK";

                    const tollEstimate = route?.tollEstimate || null;
                    const tollCurrency =
                      tollEstimate?.currency || "EUR";

                    const tollAmount = formatCurrency(
                      tollEstimate?.amount,
                      tollCurrency,
                      locale
                    );

                    const tollMinimum = formatCurrency(
                      tollEstimate?.minimum,
                      tollCurrency,
                      locale
                    );

                    const tollMaximum = formatCurrency(
                      tollEstimate?.maximum,
                      tollCurrency,
                      locale
                    );

                    const tollDistance =
                      Number.isFinite(Number(route?.tollDistanceKm))
                        ? formatKm(route.tollDistanceKm, locale)
                        : null;

                    const tollRoadNames =
                      Array.isArray(route?.tollRoadNames)
                        ? route.tollRoadNames.slice(0, 3)
                        : [];

                    return (
                      <button
                        key={route.id || index}
                        type="button"
                        className={getRouteOptionClassName(index, selectedRouteIndex)}
                        onClick={() => setSelectedRouteIndex(index)}
                      >
                        <span
                          className="ts-map-route-option-icon ts-map-route-option-icon--truck"
                          aria-hidden="true"
                        />

                        <span className="ts-map-route-option-main">
                          <span className="ts-map-route-option-top">
                            <strong>
                              {getRouteDisplayName(route, index, t)}
                            </strong>

                            {duration && (
                              <b className="ts-map-route-option-time">{duration}</b>
                            )}
                          </span>

                          <span className="ts-map-route-option-meta">
                            <em>
                              {noTrafficLabel || t("routes.noTrafficUnavailable")}
                            </em>

                            {distance && (
                              <span className="ts-map-route-option-distance">
                                {distance}
                              </span>
                            )}
                          </span>

                          {roadLabels.length > 0 && (
                            <span
                              className="ts-map-route-option-roads"
                              title={roadLabels.join(" · ")}
                            >
                              {t("routes.mainRoads", {
                                roads: roadLabels.join(" · "),
                              })}
                            </span>
                          )}

                          {hasReliableRouteData && (
                            <span className="ts-map-route-option-badges">
                              <span
                                className={`ts-map-route-badge ${
                                  hasTolls
                                    ? "is-toll"
                                    : "is-toll-free"
                                }`}
                              >
                                {hasTolls
                                  ? t("routes.tolls.with")
                                  : t("routes.tolls.withoutIdentified")}
                              </span>

                              {hasHighways && (
                                <span className="ts-map-route-badge is-highway">
                                  {t("routes.highway")}
                                </span>
                              )}
                            </span>
                          )}

                          {hasTolls && tollAmount && (
                            <span className="ts-map-route-toll-estimate">
                              <strong>
                                {t("routes.tolls.estimated", {
                                  amount: tollAmount,
                                })}
                              </strong>

                              <small>
                                {[
                                  t("routes.tolls.vehicleClass", {
                                    vehicleClass:
                                      tollEstimate?.vehicleClass || 4,
                                  }),
                                  tollDistance
                                    ? t("routes.tolls.tolledDistance", {
                                        distance: tollDistance,
                                      })
                                    : null,
                                  tollMinimum && tollMaximum
                                    ? t("routes.tolls.range", {
                                        minimum: tollMinimum,
                                        maximum: tollMaximum,
                                      })
                                    : null,
                                ]
                                  .filter(Boolean)
                                  .join(" · ")}
                              </small>

                              {tollRoadNames.length > 0 && (
                                <small>
                                  {t("routes.tolls.roads", {
                                    roads: tollRoadNames.join(" · "),
                                  })}
                                </small>
                              )}
                            </span>
                          )}

                          {hasTolls &&
                            !tollAmount &&
                            route?.tollAnalysisStatus &&
                            route.tollAnalysisStatus !== "ESTIMATED" && (
                              <span className="ts-map-route-toll-unavailable">
                                {t("routes.tolls.priceUnavailable")}
                              </span>
                            )}

                          {warningLabel && (
                            <span className="ts-map-route-option-warning">
                              {warningLabel}
                            </span>
                          )}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}

              {routeError && (
                <div className="ts-map-route-error">
                  {routeError}
                </div>
              )}
            </div>

            {!validPoints.length && (
              <div className="ts-map-empty-state">
                <strong>{t("empty.title")}</strong>
                <span>{t("empty.description")}</span>
              </div>
            )}
          </div>

          <button
            type="button"
            className="ts-map-points-toggle"
            onClick={() => setIsPointsPanelCollapsed((current) => !current)}
          >
            {isPointsPanelCollapsed
              ? t("points.show")
              : t("points.hide")}
          </button>

          {!isPointsPanelCollapsed && (
            <aside className="ts-map-points-panel">
              <div className="ts-map-points-panel-header">
                <div>
                  <strong>{t("points.title")}</strong>
                  <small>{t("points.reorderHint")}</small>
                </div>

                <span>
                  {isReorderingPoints
                    ? t("points.saving")
                    : t("points.withCoordinates", {
                        valid: validPoints.length,
                        total: mappedPoints.length,
                      })}
                </span>
              </div>

              {missingCoordinatePoints > 0 && (
                <div className="ts-map-warning">
                  {t("points.missingCoordinates", {
                    count: missingCoordinatePoints,
                  })}
                </div>
              )}

              {pointsReorderError && (
                <div className="ts-map-reorder-error">
                  {pointsReorderError}
                </div>
              )}

              <div ref={pointsListRef} className="ts-map-points-list">
                {mappedPoints.map((point) => {
                  const normalizedStatus = String(
                    point?.status || ""
                  ).toUpperCase();

                  const isReorderLocked =
                    point?.isFrozen ||
                    normalizedStatus === "COMPLETED" ||
                    normalizedStatus === "CANCELLED";

                  return (
                    <div
                      key={point.mapKey}
                      data-id={point.id}
                      className={[
                        "ts-map-point-card",
                        point.mapCoords ? "" : "is-missing-coordinates",
                        isReorderLocked ? "is-reorder-locked" : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                    >
                      <button
                        type="button"
                        className={`ts-map-point-drag-handle ${
                          isReorderLocked ? "is-disabled" : ""
                        }`}
                        disabled={isReorderLocked || isReorderingPoints}
                        aria-label={
                          isReorderLocked
                            ? t("points.drag.lockedAria")
                            : t("points.drag.reorderAria", {
                                number: point.mapOrderNumber,
                              })
                        }
                        title={
                          isReorderLocked
                            ? t("points.drag.lockedTitle")
                            : t("points.drag.title")
                        }
                      >
                        <FaGripLines aria-hidden="true" />
                      </button>

                      <div
                        className={`ts-map-point-index ts-map-point-status-${point.mapStatusClass}`}
                      >
                        {point.mapOrderNumber}
                      </div>

                      <div className="ts-map-point-content">
                        <div className="ts-map-point-topline">
                          <strong>{point.mapTypeLabel}</strong>
                          {point.mapStatus && (
                            <span
                              className={`ts-map-point-status-badge is-${point.mapStatusClass}`}
                            >
                              {getPointStatusLabel(point.mapStatus, t)}
                            </span>
                          )}
                        </div>

                        <p>{point.location || t("points.noLocation")}</p>

                        {point.mapAddress && <small>{point.mapAddress}</small>}

                        {(point.date || point.hour) && (
                          <small>
                            {[point.date, point.hour]
                              .filter(Boolean)
                              .join(" · ")}
                          </small>
                        )}

                        {!point.mapCoords && (
                          <em>{t("points.noCoordinates")}</em>
                        )}
                      </div>
                    </div>
                  );
                })}

                {manualViaPoints.length > 0 && (
                  <div className="ts-map-via-list-note">
                    {t("routes.manualAdjustment", {
                      count: manualViaPoints.length,
                    })}
                  </div>
                )}
              </div>
            </aside>
          )}
        </div>
      </div>
    </div>
  );
}